import { ref, push, set, update, remove, onValue, get, child, runTransaction, increment } from 'firebase/database'
import { db, tref, updateTenant } from './firebase.js'
import { parsFor as parsForResults, rankTeams as rankTeamsForResults } from './scoring.js'
import { countingScore } from './scoreFormats.js'
import { IS_GOLF_ELITE } from '../config/siteMode.js'
import { roundRobin as pairingsRoundRobin, repairByStanding as pairingsRepairByStanding, pairSeeded as pairingsPairSeeded } from './pairings.js'
import { teeSnapshot } from './course.js'
import { calcCourseHandicapRaw, calcPlayingHandicap, isValidStrokeIndex } from './handicapCalc.js'
import { buildNameToIndex, buildNameToAllowance, effectiveAllowance, resolveAllowSelfReported } from './handicapEntry.js'
import { memberAwarePairs } from './pairing.js'

// ---------- Tournaments ----------
export function watchTournaments(cb) {
  const r = tref('tournaments')
  return onValue(r, (snap) => cb(snap.val() || {}), (e) => console.warn('[db] listener error', e))
}

export function watchTournament(id, cb) {
  const r = tref(`tournaments/${id}`)
  return onValue(r, (snap) => cb(snap.val()), (e) => console.warn('[db] listener error', e))
}

// Single source of truth for "is this info safe to persist as a
// tournament?" — every tournament-create path (CreateModal here,
// createDemoTournament, seedScorecardTest, AI handleCreateTournament,
// AI handleCopyTournament) MUST call this before the set/update so a
// "Course TBD / Date TBD" shell can never land in /tournaments. Throws
// with a clear, director-readable message that surfaces in the UI.
export function ensureTournamentInfo(info) {
  if (!info || typeof info !== 'object') {
    throw new Error('Tournament info is required.')
  }
  const course = String(info.course || '').trim()
  const date = String(info.date || '').trim()
  if (!course && !date) {
    throw new Error('Cannot create tournament: course and date are both required.')
  }
  if (!course) {
    throw new Error('Cannot create tournament: course is required.')
  }
  if (!date) {
    throw new Error('Cannot create tournament: date is required.')
  }
}

export async function createTournament(info) {
  const fullInfo = { ...info, status: 'open', createdAt: Date.now() }
  // Golf Elite shared instance: published so no-login golfers can read this
  // tournament + submit scores/registration (security rules gate on info.public).
  // Additive — non-GE builds never set it, so their rules/behavior are unchanged.
  if (IS_GOLF_ELITE) fullInfo.public = true
  ensureTournamentInfo(fullInfo)
  const r = tref('tournaments')
  const node = push(r)
  await set(node, { info: fullInfo })
  return node.key
}

export async function updateTournamentInfo(id, info) {
  await update(tref(`tournaments/${id}/info`), info)
}

export async function deleteTournament(id) {
  await remove(tref(`tournaments/${id}`))
}

// ---------- Saved registration lists ----------
// Reusable team lists saved off a registration, stored at /registrationLists.
// A saved list survives its source event and can be loaded into a future
// registration via the "From Prior Event" panel. Shape:
//   /registrationLists/{listId}: { name, format, createdAt, teams: [ row… ] }
// where each row matches the registration row shape (teamName, players, …).
const REG_LISTS_PATH = 'registrationLists'

export async function saveRegistrationList({ name, format, teams }) {
  const node = push(tref(REG_LISTS_PATH))
  await set(node, {
    name: String(name || '').trim() || 'Saved List',
    format: format || null,
    teams: Array.isArray(teams) ? teams : [],
    createdAt: Date.now(),
  })
  return node.key
}

export function watchRegistrationLists(cb) {
  return onValue(tref(REG_LISTS_PATH), (snap) => cb(snap.val() || {}), (e) => console.warn('[db] listener error', e))
}

export async function deleteRegistrationList(listId) {
  await remove(tref(`${REG_LISTS_PATH}/${listId}`))
}

// ---------- Players ----------
export async function addPlayer(tournamentId, player) {
  const r = tref(`tournaments/${tournamentId}/players`)
  const node = push(r)
  await set(node, player)
  return node.key
}

export async function removePlayer(tournamentId, playerId) {
  await remove(tref(`tournaments/${tournamentId}/players/${playerId}`))
}

// ---------- Flights / Teams ----------
export async function createFlight(tournamentId, info) {
  const r = tref(`tournaments/${tournamentId}/flights`)
  const node = push(r)
  await set(node, { info })
  return node.key
}

export async function deleteFlight(tournamentId, flightId) {
  await remove(tref(`tournaments/${tournamentId}/flights/${flightId}`))
}

export async function createTeam(tournamentId, flightId, team) {
  const r = tref(`tournaments/${tournamentId}/flights/${flightId}/teams`)
  const node = push(r)
  await set(node, { info: team })
  return node.key
}

export async function deleteTeam(tournamentId, flightId, teamId) {
  await remove(tref(`tournaments/${tournamentId}/flights/${flightId}/teams/${teamId}`))
}

// ---------- Scores ----------
// 4-Ball Best Ball: each hole has p1score, p2score, bestBall (server-derived).
export async function setHoleScore(tournamentId, flightId, teamId, hole, p1score, p2score) {
  const valid = (v) => Number.isFinite(v) && v > 0
  let bestBall = null
  if (valid(p1score) && valid(p2score)) bestBall = Math.min(p1score, p2score)
  else if (valid(p1score)) bestBall = p1score
  else if (valid(p2score)) bestBall = p2score
  await set(
    tref(`tournaments/${tournamentId}/flights/${flightId}/teams/${teamId}/scores/${hole}`),
    {
      p1score: valid(p1score) ? p1score : null,
      p2score: valid(p2score) ? p2score : null,
      bestBall,
      updatedAt: Date.now(),
    },
  )
}

// Four-Ball family (3–4 balls per hole). `balls` = [p1,p2,p3,p4] gross; bestBall
// is derived from the team's counting mode (best1/best2/best3/aggregate/1234) so
// the leaderboard's existing bestBall path ranks it with no board changes. p3/p4
// are additive — existing 1–2-ball formats and PBCC never write them.
export async function setPlayerHoleScores(tournamentId, flightId, teamId, hole, balls, { mode = 'best1', n } = {}) {
  const valid = (v) => Number.isFinite(v) && v > 0
  const nums = (balls || []).slice(0, 4).map((b) => Number(b))
  const bestBall = countingScore(nums, mode, { holeIndex: hole - 1, n })
  await set(
    tref(`tournaments/${tournamentId}/flights/${flightId}/teams/${teamId}/scores/${hole}`),
    {
      p1score: valid(nums[0]) ? nums[0] : null,
      p2score: valid(nums[1]) ? nums[1] : null,
      p3score: valid(nums[2]) ? nums[2] : null,
      p4score: valid(nums[3]) ? nums[3] : null,
      bestBall,
      updatedAt: Date.now(),
    },
  )
}

export async function lockTeamScores(tournamentId, flightId, teamId, locked) {
  await update(
    tref(`tournaments/${tournamentId}/flights/${flightId}/teams/${teamId}/info`),
    { locked: !!locked },
  )
}

// ---------- GPS tracking ----------
export function watchTracking(tournamentId, cb) {
  const r = tref(`tournaments/${tournamentId}/tracking`)
  return onValue(r, (snap) => cb(snap.val() || {}), (e) => console.warn('[db] listener error', e))
}

export async function writeTeamLocation(tournamentId, teamId, payload) {
  const { lat, lng, hole, flightId, teamName, detectedHole, detectedHoleMeters } = payload
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
  await set(tref(`tournaments/${tournamentId}/tracking/${teamId}`), {
    lat,
    lng,
    // Manual hole the scorekeeper has selected on the scorecard.
    hole: hole ?? null,
    // GPS-derived hole — nearest corrected green to the current fix.
    // Separate field so we can verify auto-detection against the user's
    // manual selection during on-course testing without overriding it.
    detectedHole: Number.isFinite(detectedHole) ? detectedHole : null,
    // Distance in meters to the nearest corrected green. Lets the
    // Command Center surface "high confidence" vs "between holes" cues
    // without re-computing distances on read.
    detectedHoleMeters: Number.isFinite(detectedHoleMeters) ? Math.round(detectedHoleMeters) : null,
    flightId: flightId ?? null,
    teamName: teamName ?? null,
    updatedAt: Date.now(),
  })
}

export async function clearTeamLocation(tournamentId, teamId) {
  await remove(tref(`tournaments/${tournamentId}/tracking/${teamId}`))
}

// ---------- Closing a tournament with results snapshot ----------
// Computes and writes the top-3 per flight (by gross), flips status to
// 'closed', stamps closedAt. Match-play tournaments still get a stroke-based
// ranking — the closed-tournament records who finished low, regardless of
// whether the live event was scored as match or stroke.
export async function closeTournamentWithResults(tournamentId, tournament) {
  const pars = parsForResults(tournament)
  const flights = tournament?.flights || {}
  const updates = {}
  for (const [flightId, f] of Object.entries(flights)) {
    if (flightId.startsWith('_')) continue
    const teams = Object.entries(f.teams || {}).map(([id, v]) => ({ id, ...v }))
    if (teams.length === 0) continue
    const ranked = rankTeamsForResults(teams, false, pars)
    const slot = (idx) => {
      const r = ranked[idx]
      if (!r) return null
      return { teamId: r.id, teamName: r.info?.name || r.id }
    }
    const flightResults = {
      name: f.info?.name || flightId,
      first: slot(0),
      second: slot(1),
      third: slot(2),
    }
    updates[`tournaments/${tournamentId}/results/flights/${flightId}`] = flightResults
  }
  updates[`tournaments/${tournamentId}/info/status`] = 'closed'
  updates[`tournaments/${tournamentId}/info/closedAt`] = Date.now()
  await updateTenant(updates)
}

// Re-open: clears the closed flag but keeps the results snapshot for audit.
export async function reopenTournament(tournamentId) {
  await update(tref(`tournaments/${tournamentId}/info`), {
    status: 'open',
    closedAt: null,
  })
}

// ---------- Match-play pairings ----------
// Set teamA and teamB as each other's opponents. Pass null for teamB to clear
// teamA's pairing. Symmetric: also clears any stale links the previous
// opponents of A or B held.
export async function setTeamOpponent(tournamentId, flightId, teamA, teamB) {
  const flightSnap = await get(tref(`tournaments/${tournamentId}/flights/${flightId}`))
  const flight = flightSnap.val() || {}
  const teams = flight.teams || {}

  const oldOpponentOfA = teams[teamA]?.info?.opponentId || null
  const oldOpponentOfB = teamB ? (teams[teamB]?.info?.opponentId || null) : null

  const pathFor = (tid) => `tournaments/${tournamentId}/flights/${flightId}/teams/${tid}/info/opponentId`
  const updates = {}

  // Break any stale pairings the previous opponents had.
  if (oldOpponentOfA && oldOpponentOfA !== teamB) {
    updates[pathFor(oldOpponentOfA)] = null
  }
  if (oldOpponentOfB && oldOpponentOfB !== teamA) {
    updates[pathFor(oldOpponentOfB)] = null
  }

  updates[pathFor(teamA)] = teamB || null
  if (teamB) updates[pathFor(teamB)] = teamA

  await updateTenant(updates)
}

// Build the opponentId updates for one flight from the member-aware pairs (both
// directions; byes → null; stale pairings cleared first).
function opponentUpdatesFromPairs(tournamentId, flightId, teamIds, flightTeams) {
  const pathFor = (tid) => `tournaments/${tournamentId}/flights/${flightId}/teams/${tid}/info/opponentId`
  const updates = {}
  for (const tid of teamIds) updates[pathFor(tid)] = null
  for (const [a, b] of memberAwarePairs(teamIds, flightTeams)) {
    updates[pathFor(a)] = b || null
    if (b) updates[pathFor(b)] = a
  }
  return updates
}

// Pair a flight's teams by an explicit METHOD, writing the existing opponentId
// model (so the match board, bracket, and cart signs pick it up unchanged):
//   'seeded'      1↔N best-vs-worst (uses current team order)
//   'standing'    adjacent 1-2,3-4 from the supplied ranked order (re-pair by standing)
//   'round-robin' round `round`'s matchups from the full rotation
// rankedIds (optional) overrides team order (e.g. current leaderboard order).
export async function pairFlightByMethod(tournamentId, flightId, method = 'seeded', { round = 0, rankedIds = null } = {}) {
  const flight = (await get(tref(`tournaments/${tournamentId}/flights/${flightId}`))).val() || {}
  const teamIds = (rankedIds && rankedIds.length) ? rankedIds : Object.keys(flight.teams || {})
  let pairs
  if (method === 'round-robin') {
    const sched = pairingsRoundRobin(teamIds)
    pairs = sched.length ? sched[((round % sched.length) + sched.length) % sched.length] : []
  } else if (method === 'standing') {
    pairs = pairingsRepairByStanding(teamIds, 'adjacent')
  } else {
    pairs = pairingsPairSeeded(teamIds)
  }
  const base = `tournaments/${tournamentId}/flights/${flightId}/teams`
  const updates = {}
  for (const tid of teamIds) updates[`${base}/${tid}/info/opponentId`] = null
  for (const [a, b] of pairs) {
    if (a) updates[`${base}/${a}/info/opponentId`] = b || null
    if (b) updates[`${base}/${b}/info/opponentId`] = a
  }
  await updateTenant(updates)
  return pairs.length
}

export async function autoPairFlight(tournamentId, flightId) {
  const flightSnap = await get(tref(`tournaments/${tournamentId}/flights/${flightId}`))
  const flight = flightSnap.val() || {}
  const teamIds = Object.keys(flight.teams || {})
  const updates = opponentUpdatesFromPairs(tournamentId, flightId, teamIds, flight.teams)
  if (Object.keys(updates).length > 0) await updateTenant(updates)
}

// Pair every flight in one shot (member-aware). Single multi-path update so all
// flights get pairings applied in one round-trip.
export async function autoPairAllFlights(tournamentId) {
  const snap = await get(tref(`tournaments/${tournamentId}/flights`))
  const flights = snap.val() || {}
  const updates = {}
  let pairedFlights = 0
  for (const [flightId, flight] of Object.entries(flights)) {
    const teamIds = Object.keys(flight?.teams || {})
    if (teamIds.length === 0) continue
    pairedFlights++
    Object.assign(updates, opponentUpdatesFromPairs(tournamentId, flightId, teamIds, flight.teams))
  }
  if (Object.keys(updates).length > 0) await updateTenant(updates)
  return pairedFlights
}

// Set this team's startingHole and sync to its assigned opponent so a pairing
// always shares one starting hole. Pass null/undefined to clear.
export async function setTeamStartingHole(tournamentId, flightId, teamId, hole) {
  const flightSnap = await get(tref(`tournaments/${tournamentId}/flights/${flightId}`))
  const flight = flightSnap.val() || {}
  const team = flight.teams?.[teamId]
  const oppId = team?.info?.opponentId
  const cleanHole = Number.isFinite(hole) && hole >= 1 && hole <= 18 ? hole : null
  const pathFor = (tid) => `tournaments/${tournamentId}/flights/${flightId}/teams/${tid}/info/startingHole`
  const updates = {}
  updates[pathFor(teamId)] = cleanHole
  if (oppId && flight.teams?.[oppId]) updates[pathFor(oppId)] = cleanHole
  await updateTenant(updates)
}

// Group teams in a flight into "shotgun groups" before assigning starting
// holes. For match play this is the existing pair-by-opponentId logic.
// For Scramble, it's tournament-info-driven: 1, 2, or 4 teams share a
// hole based on `info.scramblePlayersPerHole`.
function buildShotgunGroups(flight, tournamentInfo) {
  const teams = Object.entries(flight?.teams || {})
  const isScramble = tournamentInfo?.format === 'Scramble'
  const teamsPerHole = isScramble
    ? Math.max(1, Math.min(4, Number(tournamentInfo?.scramblePlayersPerHole) || 1))
    : null

  if (isScramble && teamsPerHole > 1) {
    // Pure grouping by team order — chunk N teams together.
    const groups = []
    for (let i = 0; i < teams.length; i += teamsPerHole) {
      groups.push(teams.slice(i, i + teamsPerHole).map(([tid]) => tid))
    }
    return groups
  }

  // Match play (or 1-team-per-hole scramble): walk opponentId pairs.
  const seen = new Set()
  const groups = []
  for (const [tid, t] of teams) {
    if (seen.has(tid)) continue
    const oppId = t?.info?.opponentId
    if (oppId && flight.teams?.[oppId] && oppId !== tid && !isScramble) {
      groups.push([tid, oppId])
      seen.add(tid); seen.add(oppId)
    } else {
      groups.push([tid])
      seen.add(tid)
    }
  }
  return groups
}

// Walk the flight's groups and spread starting holes evenly across the
// 18-hole layout. Group size depends on tournament format / scramble settings.
export async function autoAssignShotgunHoles(tournamentId, flightId) {
  const tournamentSnap = await get(tref(`tournaments/${tournamentId}`))
  const tournament = tournamentSnap.val() || {}
  const flight = tournament.flights?.[flightId] || {}
  const groups = buildShotgunGroups(flight, tournament.info)
  if (groups.length === 0) return
  const pathFor = (tid) => `tournaments/${tournamentId}/flights/${flightId}/teams/${tid}/info/startingHole`
  const updates = {}
  groups.forEach((teamIds, i) => {
    const hole = Math.floor((i * 18) / groups.length) + 1
    teamIds.forEach((tid) => { if (tid) updates[pathFor(tid)] = hole })
  })
  await updateTenant(updates)
}

// Run the same per-flight shotgun-hole algorithm across every flight in
// one round-trip. Each flight is spread across all 18 holes independently,
// matching the single-flight button's behavior.
export async function autoAssignShotgunHolesAllFlights(tournamentId) {
  const tournamentSnap = await get(tref(`tournaments/${tournamentId}`))
  const tournament = tournamentSnap.val() || {}
  const flights = tournament.flights || {}
  const info = tournament.info || {}
  const updates = {}
  let assignedFlights = 0
  for (const [flightId, flight] of Object.entries(flights)) {
    const groups = buildShotgunGroups(flight, info)
    if (groups.length === 0) continue
    assignedFlights++
    const pathFor = (tid) => `tournaments/${tournamentId}/flights/${flightId}/teams/${tid}/info/startingHole`
    groups.forEach((teamIds, i) => {
      const hole = Math.floor((i * 18) / groups.length) + 1
      teamIds.forEach((tid) => { if (tid) updates[pathFor(tid)] = hole })
    })
  }
  if (Object.keys(updates).length > 0) await updateTenant(updates)
  return assignedFlights
}

// Assign shotgun starting holes BY MATCH so each match's two teams (4-Ball is
// team vs team — 2 players per team) always share one starting hole. Groups are
// the opponentId pairs (buildShotgunGroups). Packs from hole 1 with A/B tees, so
// a 4-team field (2 matches) lands on 1A and 1B, an 8-team field on 1A/1B/2A/2B,
// etc. Writes a numeric info.startingHole (what the scorecard opens on) plus a
// labeled tee-time assignment ("1A"/"1B") and rebuilds ONE clean 8:00 AM shotgun
// wave — replacing any stale per-position tee sheet a template clone left behind.
export async function assignShotgunHolesByMatch(tournamentId, { startTime = '8:00 AM' } = {}) {
  const snap = await get(tref(`tournaments/${tournamentId}`))
  const t = snap.val() || {}
  const info = t.info || {}
  const flights = t.flights || {}
  const groups = []
  for (const [fid, flight] of Object.entries(flights)) {
    if (fid.startsWith('_')) continue
    for (const teamIds of buildShotgunGroups(flight, info)) groups.push({ fid, teamIds })
  }
  const n = groups.length
  if (n === 0) return { matches: 0 }
  const holesUsed = Math.min(18, Math.max(1, Math.ceil(n / 2)))
  const updates = {}
  const assignments = {}
  const flightsInWave = {}
  groups.forEach((g, i) => {
    const hole = (i % holesUsed) + 1
    const letter = String.fromCharCode(65 + Math.floor(i / holesUsed)) // A, B, C…
    const label = `${hole}${letter}`
    flightsInWave[g.fid] = true
    for (const tid of g.teamIds) {
      updates[`tournaments/${tournamentId}/flights/${g.fid}/teams/${tid}/info/startingHole`] = hole
      assignments[tid] = label
    }
  })
  updates[`tournaments/${tournamentId}/teeTimes`] = {
    1: { waves: { wave1: { startTime, flights: flightsInWave, assignments } } },
  }
  await updateTenant(updates)
  return { matches: n, holesUsed }
}

// Single-team score per hole. Team enters one number representing their best
// ball for the hole — no per-player breakdown. Stored under the same path
// as setHoleScore so the leaderboard / admin grid keep working unchanged.
export async function setTeamHoleScore(tournamentId, flightId, teamId, hole, score) {
  const valid = Number.isFinite(score) && score > 0
  await set(
    tref(`tournaments/${tournamentId}/flights/${flightId}/teams/${teamId}/scores/${hole}`),
    {
      bestBall: valid ? score : null,
      updatedAt: Date.now(),
    },
  )
}

// ---------- Registrations (pre-flight signups) ----------
export function watchRegistrations(tournamentId, cb) {
  const r = tref(`tournaments/${tournamentId}/registrations`)
  return onValue(r, (snap) => cb(snap.val() || {}), (e) => console.warn('[db] listener error', e))
}

export async function addRegistration(tournamentId, reg) {
  const r = tref(`tournaments/${tournamentId}/registrations`)
  const node = push(r)
  await set(node, {
    ...reg,
    registeredAt: Date.now(),
    qualifyingStatus: reg.qualifyingStatus || 'pending',
  })
  return node.key
}

export async function updateRegistration(tournamentId, regId, patch) {
  await update(tref(`tournaments/${tournamentId}/registrations/${regId}`), patch)
}

export async function deleteRegistration(tournamentId, regId) {
  await remove(tref(`tournaments/${tournamentId}/registrations/${regId}`))
}

// Bulk add — single multi-path update so the realtime listeners fire once.
export async function bulkAddRegistrations(tournamentId, rows) {
  if (!Array.isArray(rows) || rows.length === 0) return 0
  const updates = {}
  const baseTs = Date.now()
  for (let i = 0; i < rows.length; i++) {
    const node = push(tref(`tournaments/${tournamentId}/registrations`))
    const row = rows[i]
    // Bulk imports (CSV / paste / manual table) carry a coarse team handicap
    // and are inherently unverified → mark them self-reported unless the caller
    // already set a source. (Per-player + GHIN capture comes later in the order.)
    const handicapSource = row.handicapSource
      || ((row.handicap != null || Array.isArray(row.playerHandicaps)) ? 'self' : null)
    updates[`tournaments/${tournamentId}/registrations/${node.key}`] = {
      ...row,
      ...(handicapSource ? { handicapSource } : {}),
      registeredAt: baseTs + i,
      qualifyingStatus: row.qualifyingStatus || 'pending',
    }
  }
  await updateTenant(updates)
  return rows.length
}

// ---------- Qualifying ----------
// Look up a team-or-registration by ID; the qualifying flow writes the score
// to whichever pool currently holds the team.
export async function setQualifyingScore(tournamentId, teamRef, score, dateStr) {
  // teamRef = { kind: 'reg' | 'team', regId? , flightId? , teamId? }
  const ts = dateStr || new Date().toISOString().slice(0, 10)
  if (teamRef.kind === 'reg') {
    await update(tref(`tournaments/${tournamentId}/registrations/${teamRef.regId}`), {
      qualifyingScore: Number(score) || null,
      qualifyingDate: ts,
      qualifyingStatus: 'complete',
    })
    return
  }
  if (teamRef.kind === 'team') {
    await update(
      tref(`tournaments/${tournamentId}/flights/${teamRef.flightId}/teams/${teamRef.teamId}/info`),
      {
        qualifyingScore: Number(score) || null,
        qualifyingDate: ts,
        qualifyingStatus: 'complete',
      },
    )
    return
  }
  throw new Error('Unknown teamRef kind: ' + teamRef.kind)
}

// Self-register qualifying team. Pro-shop posts a single QR; each team
// scans, enters their names, and we push a new node here. Returns the
// generated ID — the qualifying scorecard then uses it to update the same
// node when the round completes.
export async function createQualifyingTeam(tournamentId, payload) {
  const node = push(tref(`tournaments/${tournamentId}/qualifying`))
  await set(node, {
    teamName: payload.teamName || '',
    player1: payload.player1 || '',
    player2: payload.player2 || '',
    createdAt: Date.now(),
  })
  return node.key
}

// Live qualifying submissions namespace. Auto-called when the player
// finishes their 18-hole round. Uses `update` (not `set`) so the
// teamName/player1/player2/createdAt fields written by createQualifyingTeam
// are preserved alongside the final score.
export async function submitQualifyingResult(tournamentId, qualifyingTeamId, payload) {
  const path = `tournaments/${tournamentId}/qualifying/${qualifyingTeamId}`
  await update(tref(path), {
    score: payload.score,
    holes: payload.holes || null,
    teamName: payload.teamName || '',
    submittedAt: Date.now(),
  })
}

// Token-bound qualifying submit (TEST). Same node as submitQualifyingResult but
// carries WHICH teammate submitted and a manual flag: a hole-by-hole card is
// 'submitted'; an "enter final score only" card is 'pending' + manualEntry:true
// so the pro shop can eyeball and approve it. Never used on the club path.
export async function submitTeamQualifying(tournamentId, teamId, payload) {
  const path = `tournaments/${tournamentId}/qualifying/${teamId}`
  await update(tref(path), {
    score: Number(payload.score) || 0,
    holes: payload.holes || null,
    teamName: payload.teamName || '',
    byPlayer: payload.byPlayer || '',
    manualEntry: !!payload.manual,
    status: payload.manual ? 'pending' : 'submitted',
    submittedAt: Date.now(),
  })
}

export function watchQualifyingResults(tournamentId, cb) {
  const r = tref(`tournaments/${tournamentId}/qualifying`)
  return onValue(r, (snap) => cb(snap.val() || {}), (e) => console.warn('[db] listener error', e))
}

// Walk every entry in tournaments/{id}/qualifying that has a numeric
// `score` and a `submittedAt` timestamp. For each one, find the
// matching registration in tournaments/{id}/registrations by
// case-insensitive teamName (via normalizeTeamKey). On match, write
// qualifyingScore + qualifyingStatus:'complete' onto that registration.
// On miss, create a new registration with the team data. Returns a
// summary { matched, created, skipped }.
//
// One atomic multi-path update at the root so /qualify, the admin
// QualifyingTab, and the AI all see consistent state in a single
// listener tick.
export async function acceptAllQualifyingScores(tournamentId) {
  const [qSnap, rSnap] = await Promise.all([
    get(tref(`tournaments/${tournamentId}/qualifying`)),
    get(tref(`tournaments/${tournamentId}/registrations`)),
  ])
  const qualifying = qSnap.val() || {}
  const registrations = rSnap.val() || {}

  // Build a normalized index of existing registrations so we can do an
  // O(1) lookup per qualifying entry. Same key derivation as
  // autoFlightRegistrations so the two flows agree on matches.
  const regByKey = {}
  for (const [regId, r] of Object.entries(registrations)) {
    const key = normalizeTeamKey(r?.teamName || (r?.players || []).join('/'))
    if (key) regByKey[key] = { regId, reg: r }
  }

  const updates = {}
  const baseTs = Date.now()
  let matched = 0, created = 0, skipped = 0
  let createIdx = 0
  for (const [qId, q] of Object.entries(qualifying)) {
    const score = Number(q?.score)
    if (!Number.isFinite(score) || !q?.submittedAt) { skipped++; continue }
    const teamName = String(q?.teamName || '').trim()
    const key = normalizeTeamKey(teamName || ([q?.player1, q?.player2].filter(Boolean).join('/')))
    if (!key) { skipped++; continue }
    const hit = regByKey[key]
    if (hit) {
      updates[`tournaments/${tournamentId}/registrations/${hit.regId}/qualifyingScore`] = score
      updates[`tournaments/${tournamentId}/registrations/${hit.regId}/qualifyingStatus`] = 'complete'
      updates[`tournaments/${tournamentId}/registrations/${hit.regId}/qualifyingDate`] = q.submittedAt
      if (q.holes) {
        updates[`tournaments/${tournamentId}/registrations/${hit.regId}/qualifyingHoles`] = q.holes
      }
      matched++
    } else {
      // Spawn a new registration node — push() gives us a unique id
      // without colliding with any existing keys.
      const newRef = push(tref(`tournaments/${tournamentId}/registrations`))
      const players = [q?.player1, q?.player2].filter(Boolean)
      updates[`tournaments/${tournamentId}/registrations/${newRef.key}`] = {
        teamName: teamName || players.join('/') || 'Unnamed Team',
        players,
        qualifyingScore: score,
        qualifyingStatus: 'complete',
        qualifyingDate: q.submittedAt,
        qualifyingHoles: q.holes || null,
        qualifyingSubmittedAt: q.submittedAt,
        registeredAt: baseTs + createIdx,
        autoAcceptedFromQualifying: true,
      }
      createIdx++
      created++
    }
  }
  if (Object.keys(updates).length > 0) await updateTenant(updates)
  return { matched, created, skipped, totalQualifying: Object.keys(qualifying).length }
}

// Flip info.qualifyingClosed. Used by the admin "Close Qualifying"
// button + read by /qualify (QualifyApp) and the admin QualifyingTab
// to gate the QR scorecard + show the closed badge.
export async function setQualifyingClosed(tournamentId, closed) {
  await update(tref(`tournaments/${tournamentId}/info`), {
    qualifyingClosed: !!closed,
    qualifyingClosedAt: closed ? Date.now() : null,
  })
}

export async function setQualifyingStatus(tournamentId, teamRef, status) {
  const path = teamRef.kind === 'reg'
    ? `tournaments/${tournamentId}/registrations/${teamRef.regId}`
    : `tournaments/${tournamentId}/flights/${teamRef.flightId}/teams/${teamRef.teamId}/info`
  await update(tref(path), { qualifyingStatus: status })
}

// ---------- Promote a registration into a built tournament ----------
// Phase 5: a registration IS the event; "building" advances the same node to
// stage:'active' (no copy, no re-entry). When it went through a linked
// qualifying event, the approved scores are matched back onto its registrations
// by team key so the field seeds the bracket. Returns the (unchanged) id.
export async function promoteRegistrationToTournament(tournamentId, qualifyingEventId = null) {
  const updates = {}
  if (qualifyingEventId) {
    const [regSnap, qeSnap] = await Promise.all([
      get(tref(`tournaments/${tournamentId}/registrations`)),
      get(tref(`qualifyingEvents/${qualifyingEventId}/teams`)),
    ])
    const regs = regSnap.val() || {}
    const qeTeams = qeSnap.val() || {}
    const regIdByKey = {}
    for (const [regId, r] of Object.entries(regs)) {
      const key = normalizeTeamKey(r?.teamName || (r?.players || []).join('/'))
      if (key && !(key in regIdByKey)) regIdByKey[key] = regId
    }
    for (const q of Object.values(qeTeams)) {
      if (!q || !(q.approved === true || q.status === 'approved')) continue
      const key = normalizeTeamKey(q.teamName || [q.player1, q.player2].filter(Boolean).join('/'))
      const regId = regIdByKey[key]
      if (!regId) continue
      const score = Number.isFinite(Number(q.score))
        ? Number(q.score)
        : (Number.isFinite(Number(q.qualifyingScore)) ? Number(q.qualifyingScore) : null)
      if (score != null) updates[`tournaments/${tournamentId}/registrations/${regId}/qualifyingScore`] = score
      updates[`tournaments/${tournamentId}/registrations/${regId}/qualifyingStatus`] = 'complete'
      // Carry the member flag from the qualifying team back onto the registration
      // so it survives into the flight/team build.
      if (q.member != null) updates[`tournaments/${tournamentId}/registrations/${regId}/member`] = !!q.member
    }
  }
  updates[`tournaments/${tournamentId}/info/stage`] = 'active'
  await updateTenant(updates)
  return tournamentId
}

// ---------- Flight locking ----------
export async function setFlightsLocked(tournamentId, locked) {
  await update(tref(`tournaments/${tournamentId}/info`), { flightsLocked: !!locked })
  // Auto-snapshot net data the moment teams are declared final (flights
  // locked). No manual "enable net" step — the leaderboard NET toggle then
  // appears on its own. Best-effort; never blocks the lock.
  if (locked) { try { await snapshotNetHandicaps(tournamentId) } catch { /* ignore */ } }
}

// ---------- Net (handicap) scoring — STEP 4, auto-snapshot ----------
// Snapshots everything the net leaderboard needs onto the tournament in ONE
// pass: info.strokeIndex (from the linked club course) + per-team
// info.playingHandicaps (each player's Playing Handicap from their registration
// index + the course slope/rating/par and the tournament allowance). Runs
// automatically on flight-lock and after a team is added post-lock. Never
// touches gross scoring — the leaderboard only reads these when the viewer
// flips NET. Idempotent. Returns { teamsMatched, teamsTotal, strokeIndexSet }.
const normName = (s) => String(s || '').trim().toLowerCase()

export async function snapshotNetHandicaps(tournamentId) {
  const snap = await get(tref(`tournaments/${tournamentId}`))
  const t = snap.val() || {}
  const info = t.info || {}
  if ((info.handicapMode || 'none') === 'none') return { teamsMatched: 0, teamsTotal: 0, strokeIndexSet: false }
  const slopeRating = Number(info.slopeRating)
  const courseRating = Number(info.courseRating)
  const par = Number(info.coursePar)
  const allowance = Number.isFinite(Number(info.handicapAllowance)) ? Number(info.handicapAllowance) : 100

  // Name → handicap index, from every registration's parallel playerHandicaps.
  // In a GHIN-required event (self-reported not allowed) non-'ghin' sources are
  // excluded here so self-reported indexes never feed net (STEP 5). Permissive
  // events are unchanged — every finite index still feeds net.
  const nameToIndex = buildNameToIndex(t.registrations, resolveAllowSelfReported(info))
  // Per-player allowance overrides (ALLOWANCE STEP); absent → event default.
  const nameToAllowance = buildNameToAllowance(t.registrations)
  const playingHcpFor = (name) => {
    const idx = nameToIndex[normName(name)]
    if (!Number.isFinite(idx)) return null
    const raw = calcCourseHandicapRaw({ handicapIndex: idx, slopeRating, courseRating, par })
    return calcPlayingHandicap(raw, effectiveAllowance(nameToAllowance[normName(name)], allowance))
  }

  const updates = {}
  // Stroke index from the linked club course (else scoring falls back to default).
  let strokeIndexSet = false
  if (info.courseId) {
    try {
      const cs = await get(tref(`club/courses/${info.courseId}`))
      const men = cs.val()?.strokeIndexMen
      if (Array.isArray(men) && isValidStrokeIndex(men.map(Number))) {
        updates[`tournaments/${tournamentId}/info/strokeIndex`] = men.map(Number)
        strokeIndexSet = true
      }
    } catch { /* fall back to DEFAULT_STROKE_INDEX at read time */ }
  }

  // Per-team playing handicaps (parallel to info.players).
  let teamsMatched = 0
  let teamsTotal = 0
  for (const [fid, flight] of Object.entries(t.flights || {})) {
    for (const [tid, team] of Object.entries(flight?.teams || {})) {
      teamsTotal++
      const players = team?.info?.players || []
      const phs = players.map(playingHcpFor)
      if (phs.some((x) => x != null)) teamsMatched++
      updates[`tournaments/${tournamentId}/flights/${fid}/teams/${tid}/info/playingHandicaps`] = phs
    }
  }

  if (Object.keys(updates).length) await updateTenant(updates)
  return { teamsMatched, teamsTotal, strokeIndexSet }
}

// ---------- Auto-flight helpers ----------
// Normalize a team identifier so we can match a registration to its
// qualifying record by name regardless of casing / whitespace / punctuation.
export function normalizeTeamKey(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '')
}

// Convert a holes value (array or object) into a 1-indexed lookup so the
// card-off can iterate holes 1..18 uniformly.
function holesByNumber(holes) {
  const out = {}
  if (!holes) return out
  if (Array.isArray(holes)) {
    holes.forEach((v, i) => { out[i + 1] = Number(v) })
    return out
  }
  for (const [k, v] of Object.entries(holes)) {
    const n = Number(k)
    if (Number.isFinite(n)) out[n] = Number(v)
  }
  return out
}

// Three-step comparator: total → card-off (hole 1..18) → submission time.
function compareByQualifyingScore(a, b) {
  const av = Number.isFinite(Number(a.qualifyingScore)) ? Number(a.qualifyingScore) : Infinity
  const bv = Number.isFinite(Number(b.qualifyingScore)) ? Number(b.qualifyingScore) : Infinity
  if (av !== bv) return av - bv

  // Step 1 — card-off, walking holes in order.
  const ha = holesByNumber(a.qualifyingHoles)
  const hb = holesByNumber(b.qualifyingHoles)
  for (let h = 1; h <= 18; h++) {
    const sa = Number(ha[h])
    const sb = Number(hb[h])
    if (!Number.isFinite(sa) || !Number.isFinite(sb)) continue
    if (sa !== sb) return sa - sb
  }

  // Step 2 — earlier submission wins. Falls back to registration order.
  const ta = Number(a.qualifyingSubmittedAt) || Number(a.submittedAt) || Number(a.registeredAt) || Infinity
  const tb = Number(b.qualifyingSubmittedAt) || Number(b.submittedAt) || Number(b.registeredAt) || Infinity
  return ta - tb
}

// ---------- Auto-flight ----------
// Spreads registrations into N flights based on a sort key. Mutates only by
// CREATING new flights and teams; existing flights are left alone unless
// explicitly cleared first via clearFlightsExceptInfo.
//
// Qualifying-score tiebreaker (when two teams share the same total):
//   1. Card-off: walk hole 1 → 18 — the first hole where one team scored
//      lower wins and is placed higher in the flight ranking.
//   2. If still tied (all 18 holes identical, or no hole data), the earlier
//      qualifying submission wins (qualifyingSubmittedAt → submittedAt →
//      registeredAt as the final fallback).
//
// Note: this tiebreaker affects ONLY which flight a team lands in. Bracket
// seeding (1v8 / 2v7 / …) keeps using insertion order independently.
export async function autoFlightRegistrations(tournamentId, registrations, options) {
  const { numFlights, sortBy } = options
  // sortBy = 'qualifyingScore' (asc; lower is better) | 'handicap' (asc) | 'random'
  const regs = Object.entries(registrations).map(([id, r]) => ({ id, ...r }))

  // For score-based sort, pull /qualifying/ data so we can do hole-by-hole
  // card-off and submission-time tiebreakers. Demos that build registrations
  // directly skip this branch's enrichment naturally — they fall through to
  // the registeredAt fallback.
  if (sortBy === 'qualifyingScore') {
    try {
      const qsnap = await get(tref(`tournaments/${tournamentId}/qualifying`))
      const byKey = {}
      for (const q of Object.values(qsnap.val() || {})) {
        const key = normalizeTeamKey(q?.teamName || (q?.players || []).join('/'))
        if (!key) continue
        byKey[key] = q
      }
      for (const r of regs) {
        const key = normalizeTeamKey(r.teamName || (r.players || []).join('/'))
        const q = key && byKey[key]
        if (!q) continue
        if (r.qualifyingHoles == null && q.holes) r.qualifyingHoles = q.holes
        if (r.qualifyingSubmittedAt == null && q.submittedAt) r.qualifyingSubmittedAt = q.submittedAt
      }
    } catch {
      // Best-effort enrichment — sorting still works without it via the
      // step-3 timestamp fallback.
    }
  }

  let sorted
  if (sortBy === 'random') {
    sorted = regs.slice().sort(() => Math.random() - 0.5)
  } else if (sortBy === 'qualifyingScore') {
    sorted = regs.slice().sort(compareByQualifyingScore)
  } else if (sortBy === 'handicap') {
    sorted = regs.slice().sort(
      (a, b) => (Number(a.handicap) ?? 99) - (Number(b.handicap) ?? 99),
    )
  } else {
    sorted = regs.slice()
  }

  // Naming: first flight is "Championship Flight", rest are "Flight 1"..
  // ..up to "Flight 11" (12 total). The Championship Flight holds the top
  // teams (best qualifying score / lowest handicap / first random slice).
  // IDs are zero-padded so Firebase's alphabetical key order matches the
  // intended display order (flight00 < flight01 < … < flight11). Without
  // padding, "flightChampionship" sorted AFTER flight9 and pushed the
  // Championship Flight to the bottom of the list.
  const flightName = (i) => (i === 0 ? 'Championship Flight' : `Flight ${i}`)
  const baseFlightId = (i) => `flight${String(i).padStart(2, '0')}`

  // Existing flights might already exist; we'll create up to numFlights if
  // missing. No cap — caller decides the flight count (PBCC 4-Ball is a
  // hard 8 teams/flight, so 120 teams → 15 flights, etc.).
  const cappedNumFlights = numFlights
  const existing = (await get(tref(`tournaments/${tournamentId}/flights`))).val() || {}
  const flightIds = []
  const updates = {}
  for (let i = 0; i < cappedNumFlights; i++) {
    let id = baseFlightId(i)
    while (existing[id]) {
      id = `${baseFlightId(i)}_${Math.random().toString(36).slice(2, 6)}`
    }
    flightIds.push(id)
    updates[`tournaments/${tournamentId}/flights/${id}/info`] = {
      name: flightName(i),
      format: '4-Ball Best Ball',
    }
  }

  // Distribute teams across flights — top slice into Championship Flight,
  // next slice into Flight 1, and so on.
  const perFlight = Math.ceil(sorted.length / cappedNumFlights)
  let cursor = 0
  for (let f = 0; f < cappedNumFlights; f++) {
    const slice = sorted.slice(f * perFlight, (f + 1) * perFlight)
    for (const r of slice) {
      const teamId = `team_${++cursor}`
      const teamInfo = {
        name: r.teamName || `${(r.players?.[0] || 'Player')} / ${(r.players?.[1] || 'Player')}`,
        players: r.players || [],
        handicap: r.handicap ?? null,
      }
      if (Number.isFinite(Number(r.qualifyingScore))) teamInfo.qualifyingScore = Number(r.qualifyingScore)
      if (r.qualifyingStatus) teamInfo.qualifyingStatus = r.qualifyingStatus
      if (r.qualifyingDate) teamInfo.qualifyingDate = r.qualifyingDate
      if (r.member != null) teamInfo.member = !!r.member // member team (from CSV) — drives round-1 pairing
      updates[`tournaments/${tournamentId}/flights/${flightIds[f]}/teams/${teamId}/info`] = teamInfo
      // Mark the registration as flighted (don't delete — keep audit record).
      updates[`tournaments/${tournamentId}/registrations/${r.id}/flighted`] = true
      updates[`tournaments/${tournamentId}/registrations/${r.id}/flightId`] = flightIds[f]
      updates[`tournaments/${tournamentId}/registrations/${r.id}/teamId`] = teamId
    }
  }
  await updateTenant(updates)
  return { flightIds }
}

// ---------- Contests ----------
export async function setContest(tournamentId, key, value) {
  await set(tref(`tournaments/${tournamentId}/contests/${key}`), value)
}

// ---------- Tee Times ----------
// teeTimes/{day}/{flightId} = "8:00 AM"  (day is integer 1-based)
export async function setTeeTime(tournamentId, day, flightId, time) {
  const path = `tournaments/${tournamentId}/teeTimes/${day}/${flightId}`
  if (!time) await remove(tref(path))
  else await set(tref(path), time)
}

// Generate tee times for one day across all (already-sorted) flights.
// startTime is "HH:MM" 24-hour from a <input type="time">, intervalMin
// is minutes between flights. Stores formatted 12-hour strings.
export async function generateTeeTimes(tournamentId, day, flightIds, startTime, intervalMin) {
  const updates = {}
  const [hStr, mStr] = String(startTime || '').split(':')
  const baseH = Number(hStr)
  const baseM = Number(mStr)
  if (!Number.isFinite(baseH) || !Number.isFinite(baseM)) return 0
  const step = Math.max(1, Number(intervalMin) || 0)
  flightIds.forEach((fid, i) => {
    const totalMin = baseH * 60 + baseM + i * step
    const hh = Math.floor((totalMin % (24 * 60)) / 60)
    const mm = totalMin % 60
    const ampm = hh >= 12 ? 'PM' : 'AM'
    const h12 = ((hh + 11) % 12) + 1
    const formatted = `${h12}:${String(mm).padStart(2, '0')} ${ampm}`
    updates[`tournaments/${tournamentId}/teeTimes/${day}/${fid}`] = formatted
  })
  if (Object.keys(updates).length > 0) await updateTenant(updates)
  return flightIds.length
}

// ── Wave-based tee times (PBCC model) ───────────────────────────────────
//
// Data shape:
//   tournaments/{id}/teeTimes/{day}/waves/{waveKey}/
//     startTime: "8:00 AM"
//     flights:   { flightId: true }
//     assignments: { teamId: "5" | "5A" | "8:30 AM" }
//
// For shotgun tournaments waveKey is `wave1` or `wave2`. For sequential
// tournaments only `wave1` is used and assignments hold clock times instead
// of hole labels. teamId-keyed assignments make per-team lookup trivial in
// the player scorecard / QR card.

export async function setWaveStartTime(tournamentId, day, waveKey, startTime) {
  const path = `tournaments/${tournamentId}/teeTimes/${day}/waves/${waveKey}/startTime`
  if (!startTime) await remove(tref(path))
  else await set(tref(path), startTime)
}

export async function setWaveFlights(tournamentId, day, waveKey, flightIds) {
  const map = {}
  for (const fid of flightIds || []) map[fid] = true
  const path = `tournaments/${tournamentId}/teeTimes/${day}/waves/${waveKey}/flights`
  await set(tref(path), map)
}

// Replace the entire assignments map for a wave (used by auto-assign).
export async function setWaveAssignments(tournamentId, day, waveKey, assignments) {
  const path = `tournaments/${tournamentId}/teeTimes/${day}/waves/${waveKey}/assignments`
  await set(tref(path), assignments || {})
}

// Update a single group's hole/time. teamIds is an array — every team in
// the group (e.g. both teams in a match pair) gets the same value so per-
// team lookups work.
export async function setGroupAssignment(tournamentId, day, waveKey, teamIds, value) {
  const updates = {}
  for (const tid of teamIds || []) {
    const path = `tournaments/${tournamentId}/teeTimes/${day}/waves/${waveKey}/assignments/${tid}`
    if (value == null || value === '') {
      // Explicit null deletes the path on update().
      updates[path] = null
    } else {
      updates[path] = value
    }
  }
  if (Object.keys(updates).length > 0) await updateTenant(updates)
}

// PBCC-style flip rotation. Day 1 (and 3, 5, …): higher-numbered flights
// in wave 1, lower-numbered (with Championship) in wave 2. Day 2 flips.
// Returns { wave1: [flightIds], wave2: [flightIds] }.
export function pbccRotationFor(day, sortedFlightIds) {
  const half = Math.ceil(sortedFlightIds.length / 2)
  const lower = sortedFlightIds.slice(0, half) // Championship + low-numbered
  const upper = sortedFlightIds.slice(half)    // higher-numbered
  if (Number(day) % 2 === 1) {
    return { wave1: upper, wave2: lower }
  }
  return { wave1: lower, wave2: upper }
}

// Compute auto-assigned hole labels for a list of groups.
//
// Rule: NEVER place two doubled (A/B) holes back-to-back. Doubled holes
// are spread evenly across the 18 so groups stay separated around the
// course instead of stacking at the front (which would log-jam holes 1-6).
//
// Algorithm:
//   1. Compute load per hole: baseLoad = floor(N/18) on every hole, plus
//      one extra group on `extras = N % 18` holes spread evenly via
//      `idx = round(i * 18 / extras)` for i = 0..extras-1.
//   2. Walk in passes: pass p assigns one group to every hole whose load
//      > p, in hole order. The first pass fills every hole; later passes
//      visit only doubled holes — which are already spread.
//   3. Single-load holes use the bare hole number ("5"); multi-load
//      holes use letter suffixes ("5A", "5B", "5C", …).
//
// For 24 groups on 18 holes: baseLoad=1, extras=6 → doubled holes at
// 1, 4, 7, 10, 13, 16 (every 3rd hole). The other 12 holes hold one
// group each. Order of `groups` decides which group lands first on each
// hole — pass them in flight order.
//
// `groups` is an array of { key, teamIds }. Returns a flat assignments
// map { teamId: "5" | "5A" | … }.
export function assignWaveHoles(groups) {
  const out = {}
  const n = groups.length
  if (n === 0) return out

  const baseLoad = Math.floor(n / 18)
  const extras = n % 18
  const holeLoad = Array(18).fill(baseLoad)
  for (let i = 0; i < extras; i++) {
    const idx = Math.round((i * 18) / extras) % 18
    holeLoad[idx]++
  }

  const maxPasses = Math.max(...holeLoad)
  const assigned = Array(18).fill(0)
  let groupIdx = 0
  for (let pass = 0; pass < maxPasses; pass++) {
    for (let h = 0; h < 18; h++) {
      if (holeLoad[h] <= pass) continue
      const g = groups[groupIdx++]
      if (!g) return out
      const label = holeLoad[h] === 1
        ? String(h + 1)
        : `${h + 1}${String.fromCharCode(65 + assigned[h])}`
      assigned[h]++
      for (const tid of g.teamIds || []) out[tid] = label
    }
  }
  return out
}

// Shotgun assignment — the SAME method the 4-Ball tee sheet's "Auto-Assign Holes"
// button uses: group teams with buildWaveGroups (4-Ball pairs 2 teams per group
// via sequential pairing; Scramble/solo = 1 team per group), spread across 18
// holes with assignWaveHoles (even load balance, no skipped holes), then write
// each team's startingHole + a wave1 shotgun for EVERY tournament day (1..N, from
// info.tournamentDays) so all Day 1/2/3 tee tabs populate like a big multi-day
// event. Best Ball → 2 teams (4 players)/hole; Scramble 4-man → 1 team/hole.
export async function assignShotgunHoles(tournamentId, { startTime = '8:00 AM' } = {}) {
  const snap = await get(tref(`tournaments/${tournamentId}`))
  const t = snap.val() || {}
  const info = t.info || {}
  const flights = t.flights || {}
  const flightIds = Object.keys(flights).filter((k) => !k.startsWith('_'))
  const groups = buildWaveGroups(flightIds, flights, info)
  const assignments = assignWaveHoles(groups)
  const flightsMap = {}
  for (const fid of flightIds) flightsMap[fid] = true
  const days = Math.max(1, Math.min(8, Number(info.tournamentDays) || 1))
  const updates = {}
  // Numeric opening hole per team (scorecard fallback / Day 1).
  for (const [fid, flight] of Object.entries(flights)) {
    for (const tid of Object.keys(flight.teams || {})) {
      const lab = assignments[tid]
      if (lab != null) {
        updates[`tournaments/${tournamentId}/flights/${fid}/teams/${tid}/info/startingHole`] = parseInt(String(lab), 10) || 1
      }
    }
  }
  // Per-day wave-1 shotgun so every Day 1..N tee tab has its own tee times.
  for (let d = 1; d <= days; d++) {
    updates[`tournaments/${tournamentId}/teeTimes/${d}/waves/wave1/startTime`] = startTime
    updates[`tournaments/${tournamentId}/teeTimes/${d}/waves/wave1/flights`] = flightsMap
    updates[`tournaments/${tournamentId}/teeTimes/${d}/waves/wave1/assignments`] = assignments
  }
  if (Object.keys(updates).length > 0) await updateTenant(updates)
  return { groups: groups.length, days }
}

// Sequential mode: assign clock times to each group based on a start time
// and an interval. Returns { teamId: "8:30 AM" }.
export function assignWaveSequentialTimes(groups, startTime, intervalMin) {
  const [hStr, mStr] = String(startTime || '').split(':')
  const baseH = Number(hStr)
  const baseM = Number(mStr)
  const step = Math.max(1, Number(intervalMin) || 0)
  if (!Number.isFinite(baseH) || !Number.isFinite(baseM)) return {}
  const out = {}
  groups.forEach((g, i) => {
    const totalMin = baseH * 60 + baseM + i * step
    const hh = Math.floor((totalMin % (24 * 60)) / 60)
    const mm = totalMin % 60
    const ampm = hh >= 12 ? 'PM' : 'AM'
    const h12 = ((hh + 11) % 12) + 1
    const formatted = `${h12}:${String(mm).padStart(2, '0')} ${ampm}`
    for (const tid of g.teamIds || []) out[tid] = formatted
  })
  return out
}

// Build the list of groups for a flight set. A "group" is one match in
// match-play (paired teams) or one team in solo formats. Returned in
// flight order (Championship first), then by match order within a flight.
//
// Match-play pairing strategy:
//   1. First pass: pair any two teams whose opponentId fields point at
//      each other (admin has explicitly paired them).
//   2. Fallback: pair the remaining teams sequentially in iteration order
//      (team 1 with team 2, team 3 with team 4, …). This guarantees N/2
//      match rows even when admin hasn't run Auto-Pair, instead of
//      degrading into N solo rows that misrepresent the bracket.
//
// Solo formats (Scramble, Stroke Play Individual) always get one row
// per team — there is no "match" concept, every team plays its own card.
//
// Each group: { key, flightId, flightName, matchLabel, label, teamIds, teamNames }
export function buildWaveGroups(flightIds, flightsByIdOrList, tournamentInfo) {
  const flightsById = Array.isArray(flightsByIdOrList)
    ? Object.fromEntries(flightsByIdOrList.map((f) => [f.id, f]))
    : flightsByIdOrList
  const isSolo = (tournamentInfo?.format === 'Scramble') || (tournamentInfo?.format === 'Stroke Play Individual')
  const sortedIds = (flightIds || []).slice().sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
  const groups = []

  for (const fid of sortedIds) {
    const f = flightsById[fid]
    if (!f) continue
    const teams = Object.entries(f.teams || {})

    if (isSolo) {
      teams.forEach(([tid, t], i) => {
        groups.push({
          key: `${fid}__${tid}`,
          flightId: fid,
          flightName: f.info?.name || fid,
          matchLabel: `Match ${i + 1}`,
          label: t?.info?.name || tid,
          teamIds: [tid],
          teamNames: [t?.info?.name || tid],
        })
      })
      continue
    }

    const teamMap = Object.fromEntries(teams)
    const seen = new Set()
    const flightGroups = []

    // Pass 1: opponentId-based pairing (mutual references only).
    for (const [tid, t] of teams) {
      if (seen.has(tid)) continue
      const oppId = t?.info?.opponentId
      if (!oppId || oppId === tid || !teamMap[oppId] || seen.has(oppId)) continue
      const opp = teamMap[oppId]
      seen.add(tid); seen.add(oppId)
      flightGroups.push({
        flightId: fid,
        flightName: f.info?.name || fid,
        teamIds: [tid, oppId],
        teamNames: [t.info?.name || tid, opp.info?.name || oppId],
      })
    }

    // Pass 2: sequentially pair anyone left over (and emit one solo row
    // for an odd remainder, which only happens with odd team counts).
    const leftover = teams.filter(([tid]) => !seen.has(tid))
    for (let i = 0; i < leftover.length; i += 2) {
      const [tidA, tA] = leftover[i]
      const next = leftover[i + 1]
      if (next) {
        const [tidB, tB] = next
        flightGroups.push({
          flightId: fid,
          flightName: f.info?.name || fid,
          teamIds: [tidA, tidB],
          teamNames: [tA?.info?.name || tidA, tB?.info?.name || tidB],
        })
      } else {
        flightGroups.push({
          flightId: fid,
          flightName: f.info?.name || fid,
          teamIds: [tidA],
          teamNames: [tA?.info?.name || tidA],
        })
      }
    }

    flightGroups.forEach((g, i) => {
      const matchLabel = `Match ${i + 1}`
      const label = g.teamNames.length === 2
        ? `${g.teamNames[0]} vs ${g.teamNames[1]}`
        : g.teamNames[0]
      const sortedKey = g.teamIds.slice().sort().join('-')
      groups.push({ ...g, key: `${fid}__${sortedKey}`, matchLabel, label })
    })
  }
  return groups
}

// Build groups for Day 2/3 from the bracket rounds. Day 1 uses
// buildWaveGroups (opponentId-based); Day 2+ matchups are derived from the
// flight bracket so we can auto-generate the wave directly from results
// rather than asking admin to re-pair via opponentId.
//
// Returns the same group shape as buildWaveGroups so the UI can reuse the
// existing GroupRow / hole-assignment renderers.
export function buildBracketWaveGroups(flightIds, flightsById, brackets, day) {
  const sortedIds = (flightIds || []).slice().sort()
  const targetDay = Number(day)
  const groups = []
  for (const fid of sortedIds) {
    const flight = flightsById[fid]
    if (!flight) continue
    const teams = flight.teams || {}
    const flightName = flight.info?.name || fid
    const bracket = brackets?.[fid] || {}
    const rounds = bracket.rounds || {}

    const dayRounds = Object.entries(rounds)
      .filter(([, r]) => Number(r?.day) === targetDay)
      .sort(([, a], [, b]) => (a?.order || 0) - (b?.order || 0))

    let idx = 0
    for (const [roundKey, round] of dayRounds) {
      const matches = round.matches || {}
      const matchEntries = Object.entries(matches)
        .sort(([, a], [, b]) => (a?.slot || 0) - (b?.slot || 0))
      for (const [matchId, m] of matchEntries) {
        const a = m?.a, b = m?.b
        if (!a || !b) continue
        const teamA = teams[a], teamB = teams[b]
        if (!teamA || !teamB) continue
        idx++
        groups.push({
          key: `${fid}__${roundKey}__${matchId}`,
          flightId: fid,
          flightName,
          matchLabel: round.label ? `${round.label}` : `Match ${idx}`,
          label: `${teamA.info?.name || a} vs ${teamB.info?.name || b}`,
          teamIds: [a, b],
          teamNames: [teamA.info?.name || a, teamB.info?.name || b],
        })
      }
    }
  }
  return groups
}

// Score-derived winner of a head-to-head best-ball match. Returns winner's
// teamId, or null if the match is still in play / tied after 18.
function matchWinnerByScores(teamA, teamB) {
  let aWins = 0, bWins = 0, played = 0
  for (let h = 1; h <= 18; h++) {
    const sa = teamA?.scores?.[h]?.bestBall
    const sb = teamB?.scores?.[h]?.bestBall
    if (!Number.isFinite(sa) || !Number.isFinite(sb)) continue
    played++
    if (sa < sb) aWins++
    else if (sb < sa) bWins++
  }
  const remaining = 18 - played
  if (aWins - bWins > remaining) return teamA.id
  if (bWins - aWins > remaining) return teamB.id
  if (played === 18 && aWins !== bWins) return aWins > bWins ? teamA.id : teamB.id
  return null
}

// Walk a wave's flights and report which Round 1 matches still need a
// winner. Used by the "Auto-Generate" button to decide enabled/disabled.
// Returns { ready, totalMatches, undeterminedMatches }.
export function waveAdvanceReadiness(tournament, sourceDay, sourceWaveKey) {
  const teeTimes = tournament?.teeTimes || {}
  const sourceWave = teeTimes?.[sourceDay]?.waves?.[sourceWaveKey] || {}
  const flightIds = Object.keys(sourceWave.flights || {})
  if (flightIds.length === 0) return { ready: false, totalMatches: 0, undeterminedMatches: 0 }
  // Day 1 → r1 ; Day 2 → wb2/cb2 ; Day 3 → champ/consf/third/fifth.
  const targetDay = Number(sourceDay) + 1
  const flights = tournament.flights || {}
  const brackets = tournament.brackets || {}
  let total = 0
  let undetermined = 0
  for (const fid of flightIds) {
    const rounds = brackets?.[fid]?.rounds || {}
    // Find rounds whose day equals sourceDay — those are the matches we
    // need winners for, regardless of bracket-template depth.
    for (const [, round] of Object.entries(rounds)) {
      if (Number(round?.day) !== Number(sourceDay)) continue
      const matches = round.matches || {}
      for (const [, m] of Object.entries(matches)) {
        if (!m?.a || !m?.b) continue
        total++
        if (m.winnerId) continue
        const teamA = { id: m.a, ...(flights[fid]?.teams?.[m.a] || {}) }
        const teamB = { id: m.b, ...(flights[fid]?.teams?.[m.b] || {}) }
        if (!matchWinnerByScores(teamA, teamB)) undetermined++
      }
    }
  }
  return {
    ready: total > 0 && undetermined === 0,
    totalMatches: total,
    undeterminedMatches: undetermined,
    targetDay,
  }
}

// Source-wave-based auto-generate for Day 2/3 tee times.
//   1. For each flight in the source wave, fill any missing winnerId on
//      that source day's bracket matches via score-derived winners
//      (manual winnerId already set takes precedence).
//   2. Advance the bracket so the next day's match a/b slots fill.
//   3. Build groups from the bracket for the target day.
//   4. PBCC flip: source wave1 → target wave2, source wave2 → target wave1.
//   5. Assign holes via the existing lowest-load picking.
//   6. Write the target wave's flights/startTime/assignments.
//
// Returns { ok, reason, message?, targetDay, targetWaveKey, groupsCount }.
export async function autoGenerateNextDayFromWave(tournamentId, sourceDay, sourceWaveKey) {
  const tournSnap = await get(tref(`tournaments/${tournamentId}`))
  const tournament = tournSnap.val() || {}
  const flights = tournament.flights || {}
  const teeTimes = tournament.teeTimes || {}

  const sourceWave = teeTimes?.[sourceDay]?.waves?.[sourceWaveKey] || {}
  const flightIds = Object.keys(sourceWave.flights || {})
  if (flightIds.length === 0) {
    return { ok: false, reason: 'source-wave-empty', message: `Day ${sourceDay} ${sourceWaveKey} has no flights checked in.` }
  }

  // Score-derived winnerId fill (only where missing).
  const winnerFills = {}
  for (const fid of flightIds) {
    const bracket = tournament.brackets?.[fid] || {}
    const rounds = bracket.rounds || {}
    for (const [roundKey, round] of Object.entries(rounds)) {
      if (Number(round?.day) !== Number(sourceDay)) continue
      for (const [matchId, m] of Object.entries(round.matches || {})) {
        if (m?.winnerId || !m?.a || !m?.b) continue
        const teamA = { id: m.a, ...(flights[fid]?.teams?.[m.a] || {}) }
        const teamB = { id: m.b, ...(flights[fid]?.teams?.[m.b] || {}) }
        const winnerId = matchWinnerByScores(teamA, teamB)
        if (winnerId) {
          winnerFills[`tournaments/${tournamentId}/brackets/${fid}/rounds/${roundKey}/matches/${matchId}/winnerId`] = winnerId
        }
      }
    }
  }
  if (Object.keys(winnerFills).length > 0) await updateTenant(winnerFills)

  // Advance brackets so target-day match a/b slots populate.
  for (const fid of flightIds) {
    await advanceBracket(tournamentId, fid)
  }

  // Re-read fresh bracket state.
  const updatedSnap = await get(tref(`tournaments/${tournamentId}`))
  const updatedTournament = updatedSnap.val() || {}
  const updatedBrackets = updatedTournament.brackets || {}
  const updatedFlights = updatedTournament.flights || {}

  const targetDay = Number(sourceDay) + 1
  const targetWaveKey = sourceWaveKey === 'wave1' ? 'wave2' : 'wave1'
  const groups = buildBracketWaveGroups(flightIds, updatedFlights, updatedBrackets, targetDay)
  if (groups.length === 0) {
    return {
      ok: false,
      reason: 'no-groups',
      message: `No Day ${targetDay} matchups could be derived. Check that all Day ${sourceDay} ${sourceWaveKey} matches have winners set in the Bracket tab, then try again.`,
    }
  }

  const assignments = assignWaveHoles(groups)

  // PBCC default start times match the wave key (Wave 1 = AM, Wave 2 = PM).
  // Carry over any existing target-wave start time, or fall back to the
  // matching same-slot start time from the prior day.
  const existingTarget = teeTimes?.[targetDay]?.waves?.[targetWaveKey] || {}
  const priorSameSlot = teeTimes?.[sourceDay]?.waves?.[targetWaveKey] || {}
  const startTime = existingTarget.startTime
    || priorSameSlot.startTime
    || (targetWaveKey === 'wave1' ? '8:00 AM' : '1:00 PM')

  const flightsMap = {}
  for (const fid of flightIds) flightsMap[fid] = true

  const updates = {}
  const basePath = `tournaments/${tournamentId}/teeTimes/${targetDay}/waves/${targetWaveKey}`
  updates[`${basePath}/flights`] = flightsMap
  updates[`${basePath}/startTime`] = startTime
  updates[`${basePath}/assignments`] = assignments
  await updateTenant(updates)

  return { ok: true, targetDay, targetWaveKey, groupsCount: groups.length }
}

// Look up a single team's tee time for every day across all configured
// waves. Returns array of { day, startTime, holeOrTime, isShotgun }.
export function lookupTeamTeeTimes(teeTimesData, startType, flightId, teamId) {
  const isShotgun = startType !== 'sequential'
  const out = []
  if (!teeTimesData) return out
  const days = Object.keys(teeTimesData).map(Number).filter(Number.isFinite).sort((a, b) => a - b)
  for (const day of days) {
    const dayData = teeTimesData[day] || {}
    const waves = dayData.waves || {}
    for (const [waveKey, w] of Object.entries(waves)) {
      const flightsList = w?.flights || {}
      if (!flightsList[flightId]) continue
      const value = w?.assignments?.[teamId] || ''
      out.push({
        day,
        waveKey,
        startTime: w.startTime || '',
        holeOrTime: value,
        isShotgun,
      })
      break
    }
  }
  return out
}

// ---------- Brackets ----------
// Skeleton templates per bracketDays. Each round becomes
// brackets/{flightId}/rounds/{key} with empty matches that admin or
// advanceBracket() will fill.
const BRACKET_TEMPLATES = {
  1: [
    { key: 'r1',    day: 1, label: 'Round 1',    order: 0, matchCount: 4 },
    { key: 'sf',    day: 1, label: 'Semifinals', order: 1, matchCount: 2 },
    { key: 'f',     day: 1, label: 'Final',      order: 2, matchCount: 1 },
  ],
  2: [
    { key: 'r1',    day: 1, label: 'Round 1',           order: 0, matchCount: 4 },
    { key: 'wb2',   day: 2, label: 'Winners',           order: 1, matchCount: 2 },
    { key: 'cb2',   day: 2, label: 'Consolation',       order: 2, matchCount: 2 },
  ],
  3: [
    { key: 'r1',    day: 1, label: 'Round 1',              order: 0, matchCount: 4 },
    { key: 'wb2',   day: 2, label: 'Winners Bracket',      order: 1, matchCount: 2 },
    { key: 'cb2',   day: 2, label: 'Consolation Bracket',  order: 2, matchCount: 2 },
    { key: 'champ', day: 3, label: 'Championship',         order: 3, matchCount: 1 },
    { key: 'consf', day: 3, label: 'Consolation Final',    order: 4, matchCount: 1 },
    { key: 'third', day: 3, label: '3rd Place',            order: 5, matchCount: 1 },
    { key: 'fifth', day: 3, label: '5th Place',            order: 6, matchCount: 1 },
  ],
  4: [
    { key: 'r1',    day: 1, label: 'Round 1',              order: 0, matchCount: 4 },
    { key: 'wb2',   day: 2, label: 'Winners Bracket',      order: 1, matchCount: 2 },
    { key: 'cb2',   day: 2, label: 'Consolation Bracket',  order: 2, matchCount: 2 },
    { key: 'champ', day: 4, label: 'Championship Final',   order: 3, matchCount: 1 },
    { key: 'consf', day: 4, label: 'Consolation Final',    order: 4, matchCount: 1 },
    { key: 'third', day: 4, label: '3rd Place',            order: 5, matchCount: 1 },
    { key: 'fifth', day: 4, label: '5th Place',            order: 6, matchCount: 1 },
  ],
}

export function bracketTemplate(days) {
  return BRACKET_TEMPLATES[days] || BRACKET_TEMPLATES[1]
}

// Seed Day-1 round 1 with the top 8 teams of a flight using 1v8/2v7/3v6/4v5
// (insertion order = qualifying-score order from autoFlightRegistrations).
//
// Note: placeholder matches store `slot` (a non-null sentinel). Firebase
// strips null values from set() payloads, so a match like
// `{ a: null, b: null, winnerId: null }` would be dropped entirely and the
// downstream rounds would never appear. The `slot` field guarantees the
// match object persists; advanceBracket and admin edits then add a/b/winnerId.
export async function generateBracket(tournamentId, flightId, bracketDays) {
  const flightSnap = await get(tref(`tournaments/${tournamentId}/flights/${flightId}`))
  const flight = flightSnap.val() || {}
  const teamIds = Object.keys(flight.teams || {}).slice(0, 8)
  while (teamIds.length < 8) teamIds.push(null) // tolerate short flights — admin can fill
  const tmpl = bracketTemplate(bracketDays)
  const rounds = {}
  for (const r of tmpl) {
    const matches = {}
    for (let i = 0; i < r.matchCount; i++) {
      const mid = `m${i + 1}`
      matches[mid] = { slot: i + 1 }
    }
    rounds[r.key] = { day: r.day, label: r.label, order: r.order, matchCount: r.matchCount, matches }
  }
  // Round 1 seeding. If the flight has already been paired (member-aware
  // opponentId is set — so no Member team meets a Member team), seed R1 from
  // those pairings; otherwise use positional 1v8/2v7/3v6/4v5.
  const teams = flight.teams || {}
  const realIds = Object.keys(teams).slice(0, 8)
  const realSet = new Set(realIds)
  const anyPaired = realIds.some((tid) => teams[tid]?.info?.opponentId)
  let r1pairs
  if (anyPaired) {
    const seen = new Set()
    r1pairs = []
    for (const tid of realIds) {
      if (seen.has(tid)) continue
      const opp = teams[tid]?.info?.opponentId
      if (opp && realSet.has(opp) && !seen.has(opp)) { r1pairs.push([tid, opp]); seen.add(tid); seen.add(opp) }
      else { r1pairs.push([tid, null]); seen.add(tid) } // bye
    }
  } else {
    r1pairs = [[teamIds[0], teamIds[7]], [teamIds[1], teamIds[6]], [teamIds[2], teamIds[5]], [teamIds[3], teamIds[4]]]
  }
  r1pairs.slice(0, 4).forEach(([a, b], i) => {
    if (a != null && b != null) rounds.r1.matches[`m${i + 1}`] = { slot: i + 1, a, b }
  })
  await set(tref(`tournaments/${tournamentId}/brackets/${flightId}`), {
    bracketDays,
    generatedAt: Date.now(),
    rounds,
  })
}

const otherSide = (m) => (m.winnerId === m.a ? m.b : m.winnerId === m.b ? m.a : null)

// Walk the bracket and propagate winners (and losers, for consolation paths)
// downstream. Idempotent. Admin manual edits to subsequent matches are
// preserved unless those slots are still null.
//
// Implementation notes:
//   - `effective` is rounds + every queued Day-1→Day-2 write applied locally,
//     so the Day-2→Day-3 step can read the values about to be written even
//     when the user clicks Advance Winners only once.
//   - Optional chaining handles missing intermediate Firebase paths (Firebase
//     strips null fields, so an as-yet-empty match has no `a`/`b` keys at all).
export async function advanceBracket(tournamentId, flightId) {
  const snap = await get(tref(`tournaments/${tournamentId}/brackets/${flightId}`))
  const bracket = snap.val() || {}
  const rounds = bracket.rounds || {}
  const days = Number(bracket.bracketDays) || 1
  const updates = {}
  // Local mirror of rounds that we mutate alongside Firebase updates so the
  // next stage (Day 2 → Day 3) can chain off the values queued in this stage.
  const effective = JSON.parse(JSON.stringify(rounds))

  const ensure = (path) => {
    let node = effective
    for (const seg of path) {
      if (node[seg] == null) node[seg] = {}
      node = node[seg]
    }
    return node
  }

  const fillIfEmpty = (roundKey, matchId, field, val) => {
    if (val == null) return
    const current = effective?.[roundKey]?.matches?.[matchId]?.[field]
    if (current != null) return
    updates[`tournaments/${tournamentId}/brackets/${flightId}/rounds/${roundKey}/matches/${matchId}/${field}`] = val
    // Mirror the write locally so subsequent stages can read it.
    const node = ensure([roundKey, 'matches', matchId])
    node[field] = val
  }

  const r1 = effective.r1
  if (!r1) return
  const r1m = r1.matches || {}

  if (days === 1) {
    // SF1.a=R1.m1.w, SF1.b=R1.m2.w; SF2.a=R1.m3.w, SF2.b=R1.m4.w; F=SF.w
    fillIfEmpty('sf', 'm1', 'a', r1m.m1?.winnerId)
    fillIfEmpty('sf', 'm1', 'b', r1m.m2?.winnerId)
    fillIfEmpty('sf', 'm2', 'a', r1m.m3?.winnerId)
    fillIfEmpty('sf', 'm2', 'b', r1m.m4?.winnerId)
    fillIfEmpty('f', 'm1', 'a', effective.sf?.matches?.m1?.winnerId)
    fillIfEmpty('f', 'm1', 'b', effective.sf?.matches?.m2?.winnerId)
  } else {
    // 2/3/4-day all share the same WB/CB Day-2 seeding: winners → wb2, losers → cb2
    fillIfEmpty('wb2', 'm1', 'a', r1m.m1?.winnerId)
    fillIfEmpty('wb2', 'm1', 'b', r1m.m2?.winnerId)
    fillIfEmpty('wb2', 'm2', 'a', r1m.m3?.winnerId)
    fillIfEmpty('wb2', 'm2', 'b', r1m.m4?.winnerId)
    fillIfEmpty('cb2', 'm1', 'a', otherSide(r1m.m1 || {}))
    fillIfEmpty('cb2', 'm1', 'b', otherSide(r1m.m2 || {}))
    fillIfEmpty('cb2', 'm2', 'a', otherSide(r1m.m3 || {}))
    fillIfEmpty('cb2', 'm2', 'b', otherSide(r1m.m4 || {}))

    if (days === 3 || days === 4) {
      // Day 2 → Day 3. Read winners directly off the (possibly just-written)
      // local rounds mirror, then propagate.
      const wb2m = effective.wb2?.matches || {}
      const cb2m = effective.cb2?.matches || {}
      fillIfEmpty('champ', 'm1', 'a', wb2m.m1?.winnerId)
      fillIfEmpty('champ', 'm1', 'b', wb2m.m2?.winnerId)
      fillIfEmpty('consf', 'm1', 'a', cb2m.m1?.winnerId)
      fillIfEmpty('consf', 'm1', 'b', cb2m.m2?.winnerId)
      fillIfEmpty('third', 'm1', 'a', otherSide(wb2m.m1 || {}))
      fillIfEmpty('third', 'm1', 'b', otherSide(wb2m.m2 || {}))
      fillIfEmpty('fifth', 'm1', 'a', otherSide(cb2m.m1 || {}))
      fillIfEmpty('fifth', 'm1', 'b', otherSide(cb2m.m2 || {}))
    }
  }
  if (Object.keys(updates).length > 0) await updateTenant(updates)
  return Object.keys(updates).length
}

export async function setBracketMatch(tournamentId, flightId, roundKey, matchId, patch) {
  const path = `tournaments/${tournamentId}/brackets/${flightId}/rounds/${roundKey}/matches/${matchId}`
  await update(tref(path), patch)
}

export async function deleteBracket(tournamentId, flightId) {
  await remove(tref(`tournaments/${tournamentId}/brackets/${flightId}`))
}

// ---------- One-shot reads ----------
export async function getTournamentOnce(id) {
  const snap = await get(tref(`tournaments/${id}`))
  return snap.val()
}

// ── Calcutta auction ──────────────────────────────────────────────────────
//
//   tournaments/{id}/calcutta/
//     settings/
//       enabled: true
//       auctionOpen: true|false           // gates new bids from /calcutta-bid
//       payoutStructure: [ { place: 1, pct: 50 }, … ]   // must total 100
//       buyBackEnabled: true
//       buyBackPct: 50
//     bids/
//       {teamId}/
//         buyerName, buyerKey, bidAmount,
//         buyBack, buyBackCost, updatedAt
//     payouts/
//       {teamId}/  paid, paidAt
//     paid/
//       {buyerKey}/  paid, paidAt
//
// Bidding flow (current): bidders self-bid from /calcutta-bid via
// placeCalcuttaBid (Firebase transaction — only commits when amount strictly
// exceeds current high bid). Admin can override any row from the Live Bid
// Board via setCalcuttaBid (absolute write, no contention check).
//
// Both team-level (auction wins) and buyer-level (settle-up) payment status
// are tracked separately so admins can sign one buyer "paid" without
// touching team-by-team detail.

// Stable, URL-safe buyer key derived from the typed name. Two buyers with
// the same name share the same key (treated as the same person). Admin
// can disambiguate by typing "John Smith Jr" or similar.
export function buyerKeyFromName(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64) || 'unnamed'
}

export async function setCalcuttaSettings(tournamentId, settings) {
  await update(tref(`tournaments/${tournamentId}/calcutta/settings`), settings)
}

// LIVE auction pointer — the flight + team currently "on the block". The admin
// writes it (on flight-select and on save-and-advance); the public display
// (CalcuttaLive) reads it and updates instantly. This is the whole "feed the
// selected flight + current team to the display" mechanism.
export async function setCalcuttaLive(tournamentId, { flightId = null, teamId = null } = {}) {
  await set(tref(`tournaments/${tournamentId}/calcutta/live`), { flightId, teamId, at: Date.now() })
}

// Mark a flight's auction finished (stored). Recorded so the display can show it
// as completed; does not delete any bids.
export async function finishCalcuttaFlight(tournamentId, flightId) {
  if (!flightId) return
  await set(tref(`tournaments/${tournamentId}/calcutta/finished/${flightId}`), { done: true, at: Date.now() })
}

// The flight's end-of-day RESULT — the 1st and 2nd place teams (their owners win
// the payout). Set by the admin (or the day's standings); the display reveals it
// on the flight-result screen. Pass null ids to clear.
export async function setCalcuttaFlightResult(tournamentId, flightId, { firstTeamId = null, secondTeamId = null } = {}) {
  if (!flightId) return
  const path = `tournaments/${tournamentId}/calcutta/flightResult/${flightId}`
  if (!firstTeamId && !secondTeamId) { await remove(tref(path)); return }
  await set(tref(path), { firstTeamId, secondTeamId, at: Date.now() })
}

// Replace the entire bid record for a team (or remove it when bidAmount is
// not a positive number AND there's no buyer name). Storing both buyerName
// and buyerKey lets us group paid/owed by buyer without a second lookup.
export async function setCalcuttaBid(tournamentId, teamId, bid) {
  const path = `tournaments/${tournamentId}/calcutta/bids/${teamId}`
  const hasName = bid?.buyerName && bid.buyerName.trim().length > 0
  const hasBid = Number.isFinite(Number(bid?.bidAmount)) && Number(bid.bidAmount) > 0
  if (!hasName && !hasBid) {
    await remove(tref(path))
    return
  }
  await set(tref(path), {
    buyerName: (bid.buyerName || '').trim(),
    buyerKey: buyerKeyFromName(bid.buyerName),
    bidAmount: Number(bid.bidAmount) || 0,
    buyBack: !!bid.buyBack,
    buyBackCost: Number(bid.buyBackCost) || 0,
  })
}

export async function setBuyerPaid(tournamentId, buyerKey, paid) {
  const path = `tournaments/${tournamentId}/calcutta/paid/${buyerKey}`
  if (!paid) await remove(tref(path))
  else await set(tref(path), { paid: true, paidAt: Date.now() })
}

export async function setPayoutPaid(tournamentId, teamId, paid) {
  const path = `tournaments/${tournamentId}/calcutta/payouts/${teamId}`
  if (!paid) await remove(tref(path))
  else await set(tref(path), { paid: true, paidAt: Date.now() })
}

// Compute buy-back cost given a bid amount and the configured buy-back
// percentage. Spec: "team buys back 50% of itself for 50% of sale price"
// → buyBackCost = bidAmount × buyBackPct / 100 / 2.
export function computeBuyBackCost(bidAmount, buyBackPct) {
  const pct = Number(buyBackPct) || 0
  const bid = Number(bidAmount) || 0
  return Math.round((bid * pct / 100) / 2)
}

// Compute payouts from the configured structure.
export function computePayouts(totalPot, payoutStructure) {
  return (payoutStructure || []).map((row) => ({
    place: row.place,
    pct: Number(row.pct) || 0,
    amount: Math.round((Number(totalPot) || 0) * (Number(row.pct) || 0) / 100),
  }))
}

// PBCC PER-FLIGHT Calcutta model. Each flight's pot is the winning bids on the
// teams IN that flight; MGA takes `mgaPct` off the top of each flight's pot; the
// remainder splits among the flight's places per `payoutStructure` (e.g. 70/30
// to 1st/2nd). Buy-back money is added to the flight pot only when enabled —
// same as the tournament-wide total. Returns one entry per flight (order = the
// flights array), each with { pot, mgaCut, afterMga, payouts:[{place,pct,amount}] }.
export function computeFlightPayouts(flights, bids, { mgaPct = 10, payoutStructure = [{ place: 1, pct: 70 }, { place: 2, pct: 30 }], buyBackEnabled = false } = {}) {
  const teamFlight = {}
  for (const f of flights || []) {
    for (const teamId of Object.keys(f?.teams || {})) teamFlight[teamId] = f.id
  }
  const potByFlight = {}
  for (const [teamId, bid] of Object.entries(bids || {})) {
    const fid = teamFlight[teamId]
    if (!fid) continue
    const amt = (Number(bid?.bidAmount) || 0) + (buyBackEnabled && bid?.buyBack ? (Number(bid?.buyBackCost) || 0) : 0)
    potByFlight[fid] = (potByFlight[fid] || 0) + amt
  }
  const cut = Math.max(0, Math.min(100, Number(mgaPct) || 0))
  const splits = (payoutStructure || []).map((r) => ({ place: r.place, pct: Number(r.pct) || 0 }))
  return (flights || []).map((f) => {
    const pot = potByFlight[f.id] || 0
    const mgaCut = Math.round(pot * cut / 100)
    const afterMga = pot - mgaCut
    // Allocate places; the LAST place absorbs the rounding remainder so the
    // payouts always sum EXACTLY to afterMga (no dollar lost/gained to rounding).
    let allocated = 0
    const payouts = splits.map((s, i) => {
      const amount = i === splits.length - 1 ? afterMga - allocated : Math.round(afterMga * s.pct / 100)
      allocated += amount
      return { place: s.place, pct: s.pct, amount }
    })
    return { flightId: f.id, flightName: f?.info?.name || f.id, pot, mgaPct: cut, mgaCut, afterMga, payouts }
  })
}

// Open or close the auction. When closed, placeCalcuttaBid rejects new bids
// (admin can still override via setCalcuttaBid).
export async function setAuctionOpen(tournamentId, open) {
  await update(tref(`tournaments/${tournamentId}/calcutta/settings`), { auctionOpen: !!open })
}

// Atomic bid placement from a phone. Returns { ok, reason, current }:
//   ok=true → the bid won and is now the high bid
//   ok=false, reason='outbid' → someone else bid higher first
//   ok=false, reason='auction-closed' → admin has closed the auction
//   ok=false, reason='amount-invalid' / 'name-missing'
//
// Uses runTransaction so two bidders racing for the same team never both
// "win" — exactly one transaction commits, the other returns ok=false and
// surfaces the current high bid so the page can show "you were outbid."
export async function placeCalcuttaBid(tournamentId, teamId, buyerName, bidAmount) {
  const cleanName = (buyerName || '').trim()
  const cleanKey = buyerKeyFromName(cleanName)
  const amount = Math.floor(Number(bidAmount) || 0)
  if (!cleanName) return { ok: false, reason: 'name-missing' }
  if (amount <= 0) return { ok: false, reason: 'amount-invalid' }

  const settingsSnap = await get(tref(`tournaments/${tournamentId}/calcutta/settings`))
  const settings = settingsSnap.val() || {}
  if (settings.auctionOpen === false) {
    return { ok: false, reason: 'auction-closed' }
  }

  const path = `tournaments/${tournamentId}/calcutta/bids/${teamId}`
  const tx = await runTransaction(tref(path), (cur) => {
    if (cur && Number(cur.bidAmount) >= amount) return // abort — not strictly higher
    // Preserve buy-back state if the same bidder is raising their own bid.
    const sameBidder = cur && cur.buyerKey === cleanKey
    return {
      buyerName: cleanName,
      buyerKey: cleanKey,
      bidAmount: amount,
      buyBack: sameBidder ? !!cur.buyBack : false,
      buyBackCost: sameBidder ? Number(cur.buyBackCost) || 0 : 0,
      updatedAt: Date.now(),
    }
  })
  if (tx.committed) return { ok: true, current: tx.snapshot.val() }
  return { ok: false, reason: 'outbid', current: tx.snapshot.val() }
}

// Toggle buy-back for a team — only succeeds if the caller is the current
// high bidder (guards against stale UI / race with an outbid).
export async function setBuyBackForBid(tournamentId, teamId, buyerKey, buyBack, buyBackPct) {
  const path = `tournaments/${tournamentId}/calcutta/bids/${teamId}`
  const snap = await get(tref(path))
  const cur = snap.val()
  if (!cur || cur.buyerKey !== buyerKey) return false
  const cost = buyBack ? computeBuyBackCost(cur.bidAmount, buyBackPct) : 0
  await update(tref(path), { buyBack: !!buyBack, buyBackCost: cost, updatedAt: Date.now() })
  return true
}

// Admin clears a bid row. Used to reset a bad/test bid so bidders can start over.
export async function clearCalcuttaBid(tournamentId, teamId) {
  await remove(tref(`tournaments/${tournamentId}/calcutta/bids/${teamId}`))
}

// Distinct list of bidder names from the current bids — feeds the
// "returning bidder" dropdown on /calcutta-bid.
export function distinctBidders(bids) {
  const seen = new Map()
  for (const b of Object.values(bids || {})) {
    if (!b?.buyerKey) continue
    if (!seen.has(b.buyerKey)) seen.set(b.buyerKey, b.buyerName || b.buyerKey)
  }
  return Array.from(seen.entries())
    .map(([buyerKey, buyerName]) => ({ buyerKey, buyerName }))
    .sort((a, b) => a.buyerName.localeCompare(b.buyerName))
}

// Aggregate the bids node into per-buyer summaries, sorted by total owed.
// Used by the admin payment-tracking table and the buyer-facing view.
export function aggregateBuyers(bids, paidByBuyer) {
  const map = {}
  for (const [teamId, bid] of Object.entries(bids || {})) {
    const key = bid?.buyerKey || buyerKeyFromName(bid?.buyerName)
    if (!key) continue
    if (!map[key]) {
      map[key] = {
        buyerKey: key,
        buyerName: bid?.buyerName || key,
        teams: [],          // [{ teamId, bidAmount, buyBack, buyBackCost }]
        totalOwed: 0,
      }
    }
    const owed = (Number(bid.bidAmount) || 0) + (bid.buyBack ? Number(bid.buyBackCost) || 0 : 0)
    map[key].teams.push({
      teamId,
      bidAmount: Number(bid.bidAmount) || 0,
      buyBack: !!bid.buyBack,
      buyBackCost: Number(bid.buyBackCost) || 0,
    })
    map[key].totalOwed += owed
  }
  const out = Object.values(map).map((b) => ({
    ...b,
    paid: !!(paidByBuyer?.[b.buyerKey]?.paid),
    paidAt: paidByBuyer?.[b.buyerKey]?.paidAt || null,
  }))
  // Unpaid first (alphabetical), paid last (alphabetical)
  out.sort((a, b) => {
    if (a.paid !== b.paid) return a.paid ? 1 : -1
    return (a.buyerName || '').localeCompare(b.buyerName || '')
  })
  return out
}

// ── Photos / Video Wall ─────────────────────────────────────────────────────
//
//   tournaments/{id}/photoSettings/
//     enabled: bool
//     maxPerGuest: number (default 5)
//   tournaments/{id}/photoAdmins/{key}/
//     name, pin, addedAt
//   tournaments/{id}/photos/{photoId}/
//     url, thumbnailUrl, caption, submittedBy, submitterRole,
//     teamId, hole, timestamp, status, approvedBy, approvedAt, type
//
// Storage layout: tournaments/{id}/photos/{photoId}.<ext>
//
// Submission flow:
//   • Photo Admin (URL has valid &pin=…) → status: 'approved' immediately
//   • Public guest                       → status: 'pending'  (admin reviews)
//   • Super Admin (in admin tab)         → status: 'approved' immediately

export async function setPhotoSettings(tournamentId, settings) {
  await update(tref(`tournaments/${tournamentId}/photoSettings`), settings)
}

// Tournament-wide TV display settings. Currently just the broadcast scroll
// speed for /tv and /tv/scores. Stored at tournaments/{id}/tvSettings/* so
// the live TVs pick it up via the same listener everything else uses.
export async function setTvScrollSpeed(tournamentId, speed) {
  const clean = (speed === 'medium' || speed === 'fast') ? speed : 'slow'
  await update(tref(`tournaments/${tournamentId}/tvSettings`), { scrollSpeed: clean })
}

// Persisted GROSS/NET display mode (admin-controlled). The public leaderboard
// and every TV board read tournaments/{id}/tvSettings/netMode so flipping it in
// the admin flows to the TVs too. Default (absent/false) = gross.
export async function setTvNetMode(tournamentId, on) {
  await update(tref(`tournaments/${tournamentId}/tvSettings`), { netMode: !!on })
}

// Scramble format settings. Stored at tournaments/{id}/info/* alongside other
// per-tournament info so both admin and player views see the same source of
// truth via the existing tournament listener.
//   handicapsEnabled        bool   — 2-man defaults true, 4-man false
//   mulligans               int    — 0..4 per team
//   minDriveEnabled         bool   — track per-player minimum drive contributions
//   minDrivesPerPlayer      int    — required count when enabled
export async function setScrambleSettings(tournamentId, partial) {
  const clean = {}
  if (partial.handicapsEnabled !== undefined) clean.scrambleHandicapsEnabled = !!partial.handicapsEnabled
  if (partial.mulligans !== undefined) clean.scrambleMulligans = Math.max(0, Math.min(4, Number(partial.mulligans) || 0))
  if (partial.minDriveEnabled !== undefined) clean.scrambleMinDriveEnabled = !!partial.minDriveEnabled
  if (partial.minDrivesPerPlayer !== undefined) clean.scrambleMinDrivesPerPlayer = Math.max(0, Math.min(18, Number(partial.minDrivesPerPlayer) || 0))
  if (Object.keys(clean).length === 0) return
  await update(tref(`tournaments/${tournamentId}/info`), clean)
}

// Per-team scramble counters. Mulligans-used is bounded by tournament setting;
// caller (UI) clamps before calling. Drives-used is keyed by playerIndex.
export async function setTeamMulligansUsed(tournamentId, flightId, teamId, used) {
  await update(
    tref(`tournaments/${tournamentId}/flights/${flightId}/teams/${teamId}/info`),
    { mulligansUsed: Math.max(0, Number(used) || 0) },
  )
}

// Per-player handicaps for Scramble teams. Stored as a sparse object keyed
// by player index (RTDB strips array nulls). scoring.js's
// scrambleHandicapStrokes reads it back via team.info.handicaps and
// computes the team allowance using the 35/15-style formula.
export async function setTeamPlayerHandicaps(tournamentId, flightId, teamId, handicaps) {
  const obj = {}
  ;(handicaps || []).forEach((v, i) => {
    if (v === '' || v == null) return
    const n = Number(v)
    if (Number.isFinite(n)) obj[i] = n
  })
  await update(
    tref(`tournaments/${tournamentId}/flights/${flightId}/teams/${teamId}/info`),
    { handicaps: obj },
  )
}

export async function setTeamDrivesUsed(tournamentId, flightId, teamId, drivesUsed) {
  // drivesUsed is an array indexed by player position. Store as a plain object
  // so RTDB doesn't strip null entries.
  const obj = {}
  ;(drivesUsed || []).forEach((v, i) => { obj[i] = Number(v) || 0 })
  await update(
    tref(`tournaments/${tournamentId}/flights/${flightId}/teams/${teamId}/info`),
    { drivesUsed: obj },
  )
}

export async function setPhotoSubmissionsEnabled(tournamentId, enabled) {
  await update(tref(`tournaments/${tournamentId}/photoSettings`), { enabled: !!enabled })
}

export async function addPhotoAdmin(tournamentId, name, canVideo = false) {
  const cleanName = (name || '').trim()
  if (!cleanName) return null
  const pin = String(Math.floor(1000 + Math.random() * 9000))
  const node = push(tref(`tournaments/${tournamentId}/photoAdmins`))
  await set(node, { name: cleanName, pin, canVideo: !!canVideo, addedAt: Date.now() })
  return { key: node.key, name: cleanName, pin, canVideo: !!canVideo }
}

export async function removePhotoAdmin(tournamentId, key) {
  await remove(tref(`tournaments/${tournamentId}/photoAdmins/${key}`))
}

// Returns the matched photo admin record { key, name } if pin is valid,
// or null. Used by /photos to flip submissions to immediate-approved.
export async function verifyPhotoAdminPin(tournamentId, pin) {
  const cleanPin = String(pin || '').trim()
  if (!cleanPin) return null
  const snap = await get(tref(`tournaments/${tournamentId}/photoAdmins`))
  const all = snap.val() || {}
  for (const [key, v] of Object.entries(all)) {
    if (String(v?.pin) === cleanPin) return { key, name: v.name || 'Photo Admin', canVideo: !!v.canVideo }
  }
  return null
}

// Upload a File/Blob to Storage at tournaments/{id}/photos/{photoId}.<ext>
// then write the metadata record to RTDB. submitterRole determines initial
// status: 'admin' or 'photoAdmin' → approved, anything else → pending.
export async function submitPhoto(tournamentId, file, meta = {}) {
  if (!tournamentId) throw new Error('Missing tournament ID')
  if (!file) throw new Error('No file selected')
  console.log('[submitPhoto] start', {
    tournamentId,
    fileName: file?.name,
    fileType: file?.type,
    fileSize: file?.size,
    role: meta.submitterRole,
  })
  const { uploadBytes, ref: storageRef, getDownloadURL } = await import('firebase/storage')
  const { storage } = await import('./firebase.js')
  const role = meta.submitterRole || 'public'
  const isVideo = String(file?.type || '').startsWith('video/')
  const ext = (file?.name?.match(/\.[a-zA-Z0-9]+$/)?.[0]) || (isVideo ? '.mp4' : '.jpg')
  const photoNode = push(tref(`tournaments/${tournamentId}/photos`))
  const photoId = photoNode.key
  const path = `tournaments/${tournamentId}/photos/${photoId}${ext}`
  const objRef = storageRef(storage, path)
  console.log('[submitPhoto] uploading to', path)
  try {
    await uploadBytes(objRef, file, { contentType: file?.type || (isVideo ? 'video/mp4' : 'image/jpeg') })
    console.log('[submitPhoto] upload OK')
  } catch (err) {
    console.error('[submitPhoto] uploadBytes failed', err)
    // Surface the actual cause to the UI so guests don't stare at silence.
    const code = err?.code || ''
    if (code.includes('storage/unauthorized') || code.includes('permission')) {
      throw new Error('Photo upload blocked by Firebase Storage rules. Admin must enable public photo writes.')
    }
    if (code.includes('storage/unknown') || /CORS|preflight|network/i.test(err?.message || '')) {
      throw new Error('Photo upload failed — Firebase Storage may not be enabled for this project. Admin must initialize Storage in the Firebase Console.')
    }
    if (code.includes('storage/object-not-found')) {
      throw new Error('Storage bucket not found. Check VITE_FIREBASE_STORAGE_BUCKET in .env.')
    }
    if (code.includes('storage/quota-exceeded')) {
      throw new Error('Photo storage quota exceeded. Contact tournament admin.')
    }
    throw new Error('Photo upload failed: ' + (err?.message || code || 'unknown error'))
  }
  const url = await getDownloadURL(objRef)
  console.log('[submitPhoto] download URL ready')
  const status = (role === 'admin' || role === 'photoAdmin') ? 'approved' : 'pending'
  const record = {
    url,
    thumbnailUrl: url,
    caption: (meta.caption || '').slice(0, 200),
    submittedBy: meta.submittedBy || 'Guest',
    submitterRole: role,
    teamId: meta.teamId || null,
    hole: Number.isFinite(Number(meta.hole)) ? Number(meta.hole) : null,
    timestamp: Date.now(),
    status,
    type: isVideo ? 'video' : 'photo',
    storagePath: path,
  }
  if (isVideo && Number.isFinite(Number(meta.durationSeconds))) {
    record.durationSeconds = Math.round(Number(meta.durationSeconds))
  }
  if (status === 'approved') {
    record.approvedBy = meta.approvedBy || meta.submittedBy || 'Auto'
    record.approvedAt = Date.now()
  }
  await set(photoNode, record)
  console.log('[submitPhoto] DB record saved', { photoId, status })
  return { photoId, status }
}

// PHASE 2 curation writes. Additive — never touch moderation status.
// likeCount is a shared aggregate; the client gates ±1 per device via
// localStorage (photoLocal). featured is an admin highlight pick.
export async function likePhoto(tournamentId, photoId, delta) {
  const d = delta > 0 ? 1 : -1
  await update(tref(`tournaments/${tournamentId}/photos/${photoId}`), { likeCount: increment(d) })
}

export async function setPhotoFeatured(tournamentId, photoId, featured) {
  await update(tref(`tournaments/${tournamentId}/photos/${photoId}`), { featured: !!featured })
}

export async function setPhotoStatus(tournamentId, photoId, status, approvedBy) {
  const path = `tournaments/${tournamentId}/photos/${photoId}`
  const updates = { status }
  if (status === 'approved') {
    updates.approvedBy = approvedBy || 'Admin'
    updates.approvedAt = Date.now()
  }
  await update(tref(path), updates)
}

// Bulk-approve every pending photo. Returns the count touched.
export async function approveAllPendingPhotos(tournamentId, approvedBy) {
  const snap = await get(tref(`tournaments/${tournamentId}/photos`))
  const all = snap.val() || {}
  const updates = {}
  let count = 0
  for (const [pid, p] of Object.entries(all)) {
    if (p?.status !== 'pending') continue
    updates[`tournaments/${tournamentId}/photos/${pid}/status`] = 'approved'
    updates[`tournaments/${tournamentId}/photos/${pid}/approvedBy`] = approvedBy || 'Admin'
    updates[`tournaments/${tournamentId}/photos/${pid}/approvedAt`] = Date.now()
    count++
  }
  if (Object.keys(updates).length > 0) await updateTenant(updates)
  return count
}

export async function rejectAllPendingPhotos(tournamentId) {
  const snap = await get(tref(`tournaments/${tournamentId}/photos`))
  const all = snap.val() || {}
  const updates = {}
  let count = 0
  for (const [pid, p] of Object.entries(all)) {
    if (p?.status !== 'pending') continue
    updates[`tournaments/${tournamentId}/photos/${pid}/status`] = 'rejected'
    count++
  }
  if (Object.keys(updates).length > 0) await updateTenant(updates)
  return count
}

// Hard delete: removes RTDB record AND the storage file.
export async function deletePhoto(tournamentId, photoId, storagePath) {
  await remove(tref(`tournaments/${tournamentId}/photos/${photoId}`))
  if (storagePath) {
    try {
      const { deleteObject, ref: storageRef } = await import('firebase/storage')
      const { storage } = await import('./firebase.js')
      await deleteObject(storageRef(storage, storagePath))
    } catch {
      // Storage object may already be gone — RTDB delete is the source of truth.
    }
  }
}

// ---------- Handicap & Courses ----------
// Club-wide saved courses + player roster. Lives under /club/ so it's
// completely separate from /tournaments/* — no risk of mutating an
// existing tournament's data.

export function watchClubCourses(cb) {
  const r = tref('club/courses')
  return onValue(r, (snap) => cb(snap.val() || {}), (e) => console.warn('[db] listener error', e))
}

export async function saveClubCourse(course) {
  // course = { courseName, courseId, coursePar, slopeRating, courseRating, teeName }
  const node = push(tref('club/courses'))
  await set(node, { ...course, savedAt: Date.now() })
  return node.key
}

export async function deleteClubCourse(id) {
  await remove(tref(`club/courses/${id}`))
}

// Apply a course + tee to the whole tournament: snapshot rating/slope/par +
// per-hole pars/stroke-index into info, so scoring/handicap read them (reuses
// the existing info-based path — single-course events behave as before).
export async function applyCourseToTournament(tournamentId, courseName, tee) {
  await updateTournamentInfo(tournamentId, teeSnapshot(courseName, tee))
}

// Multi-course: override ONE flight's course/tee (flight.info.course). scoring's
// parsFor/strokeIndexFor + resolveCourse prefer this per-flight snapshot.
export async function applyCourseToFlight(tournamentId, flightId, courseName, tee) {
  await update(tref(`tournaments/${tournamentId}/flights/${flightId}/info`), { course: teeSnapshot(courseName, tee) })
}

export function watchClubPlayers(cb) {
  const r = tref('club/players')
  return onValue(r, (snap) => cb(snap.val() || {}), (e) => console.warn('[db] listener error', e))
}

export async function saveClubPlayer(player) {
  // player = { name, email?, ghin?, handicapIndex? }
  const node = push(tref('club/players'))
  await set(node, { ...player, savedAt: Date.now() })
  return node.key
}

export async function updateClubPlayer(id, patch) {
  await update(tref(`club/players/${id}`), patch)
}

export async function deleteClubPlayer(id) {
  await remove(tref(`club/players/${id}`))
}

// A team's OWN "enter winner only" pick (no hole-by-hole card). Records the winner
// ONLY on the reporting team's node, so each team's entry is stored separately.
// The winner is SUBMITTED only when BOTH teams have entered the same winner (see
// officialMatchResult in ScoreApp) — one entry alone, or two that disagree, just
// reads "waiting for both teams to agree." Nothing is written to the bracket and
// nothing is flagged to the admin; players resolve any disagreement themselves.
export async function reportMatchWinner(tournamentId, flightId, teamId, winnerTeamId) {
  try {
    await update(tref(`tournaments/${tournamentId}/flights/${flightId}/teams/${teamId}/info`),
      { reportedWinnerId: winnerTeamId, reportedAt: Date.now() })
    return 'ok'
  } catch { return 'error' }
}
